import React, { useState, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  TextField,
  Autocomplete,
  Button,
  Chip,
  Card,
  Checkbox,
  Tooltip,
  MenuItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import InfoIcon from "@mui/icons-material/Info";
import AssignmentIcon from "@mui/icons-material/Assignment";
import DescriptionIcon from "@mui/icons-material/Description";
import ScienceIcon from "@mui/icons-material/Science";
import ImageIcon from "@mui/icons-material/Image";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { useNavigate, useLocation } from "react-router-dom";
import { useSnackbar } from "../../providers/SnackbarProvider";
import api from "../../api/client";
import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { OverAllLoader } from "../../components/OverAllLoader";
import QuestionPaperTemplate from "./anna-university-template/QuestionPaperTemplate";
import AddQuestionsStep from "./create/AddQuestionsStep";
import {
  sectionsFromTemplate,
  sectionsFromSavedPaper,
  buildPaperFromSections,
  buildRandomPaper,
  buildHeader,
  templateSummary,
  slotCount,
  buildPaperFromPreview,
} from "./create/paperBuilder";
import { filterDummyQuestions } from "./create/dummyData";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";

// lab_tool_id -> human label (backend: 1=Circuit, 2=Eng Graphics, 3=Civil)
const LAB_TOOL_LABELS = {
  1: "Circuit",
  2: "Engineering Graphics",
  3: "Civil",
};

const MAX_PER_ROW = 30;

// Merge a calendar date (day) and a clock time (hh:mm) into one dayjs instant.
// Used only to validate that the chosen "schedule for later" moment is in the future.
const mergeDateTime = (date, time) => {
  if (!date || !time) return null;
  const d = dayjs(date);
  const t = dayjs(time);
  if (!d.isValid() || !t.isValid()) return null;
  return d.hour(t.hour()).minute(t.minute()).second(0).millisecond(0);
};

// The paper is always scheduled with a date + time. scheduled_date ("YYYY-MM-DD") and
// scheduled_time ("HH:mm:ss" 24h) are sent in their respective keys.
const buildSchedulePayload = (data) => ({
  scheduled_date: data.latestDate ? dayjs(data.latestDate).format("YYYY-MM-DD") : null,
  scheduled_time: data.scheduleTime ? dayjs(data.scheduleTime).format("HH:mm:ss") : null,
});

const schema = Yup.object().shape({
  program: Yup.object().nullable().when(["isEditMode", "chooseFromExistingQB"], {
    is: (isEditMode, chooseFromExistingQB) => !isEditMode && chooseFromExistingQB,
    then: (s) => s.required("Program is required"),
    otherwise: (s) => s.nullable(),
  }),
  // Not required, and no longer has a field: create derives it from the chosen question
  // banks (which are required) and update keeps the saved paper's. Requiring it here would
  // block submit with an error the user has no control to fix.
  primaryCategory: Yup.object().nullable(),
  assignedQuestionBank: Yup.array().when(["isEditMode", "chooseFromExistingQB"], {
    is: (isEditMode, chooseFromExistingQB) => !isEditMode && !chooseFromExistingQB,
    then: (s) => s.min(1, "Select at least one assigned question bank"),
    otherwise: (s) => s,
  }),
  examType: Yup.string().nullable().required("Assessment type is required"),
  cameraEnabled: Yup.string().nullable().required("Camera setting is required"),
  timeDuration: Yup.number()
    .typeError("Must be a number")
    .nullable()
    .required("Time duration is required")
    .min(1, "Minimum 1 minute"),
  // Schedule Date — always required.
  latestDate: Yup.date()
    .nullable()
    .required("Schedule date is required")
    .when("isEditMode", {
      is: false,
      then: (schema) => schema.test("future-date", "Schedule date must be today or a future date", function (value) {
        if (!value) return true;
        const selectedDate = dayjs(value).startOf("day");
        const today = dayjs().startOf("day");
        return selectedDate.isSame(today) || selectedDate.isAfter(today);
      }),
      otherwise: (schema) => schema,
    }),
  // Schedule Time — always required. The future-time check applies only when creating; in
  // edit mode the scheduled moment may already be in the past (e.g. an OPEN paper) and must
  // not block updates like changing the status to COMPLETED.
  scheduleTime: Yup.date()
    .nullable()
    .required("Schedule time is required")
    .when("isEditMode", {
      is: false,
      then: (schema) =>
        schema.test("future-datetime", "Scheduled date & time must be in the future", function (value) {
          const { latestDate } = this.parent;
          if (!latestDate || !value) return true;
          const when = mergeDateTime(latestDate, value);
          return when ? when.isAfter(dayjs()) : true;
        }),
      otherwise: (schema) => schema,
    }),
  status: Yup.string().when("isEditMode", {
    is: true,
    then: (schema) => schema.required("Status is required"),
    otherwise: (schema) => schema.nullable(),
  }),
  cancelReason: Yup.string().when("status", {
    is: "CANCELLED",
    then: (schema) => schema.required("Cancellation reason is required"),
    otherwise: (schema) => schema.nullable(),
  }),
});

// Mapped/Assigned mode: reshape a QuestionBankSessionsList row into the same
// { id, name, code } the Subject Autocomplete produces — the breakdown fetch and the
// section browser both gate on that shape, and it supplies the top-level category_id.
// Banks may span subjects; each one carries its own category_id in the ids[] payload.
const categoryFromAssignedQBs = (qbs) => {
  const qb = (qbs || [])[0];
  return qb?.category_id
    ? { id: qb.category_id, name: qb.category || "", code: qb.category_code || "" }
    : null;
};

// Mapped/Assigned mode scope: one { category_id, gen_id } entry per selected bank.
const assignedBankIds = (qbs) =>
  (qbs || [])
    .filter((qb) => qb.session_key)
    .map((qb) => ({ category_id: qb.category_id, gen_id: qb.session_key }));

// QuestionBankSessionsList rows carry program_name / program_code / program_display_name
// but not always program_id, which the backend requires. Recover it by matching the row
// against the programs dropdown (GetAssignedProgramsDropdown), which always has the id.
const resolveProgramId = (qb, programs) => {
  if (!qb) return null;
  if (qb.program_id != null) return qb.program_id;
  const match = (programs || []).find(
    (p) =>
      (qb.program_code && p.program_code === qb.program_code) ||
      (qb.program_name && p.program_name === qb.program_name) ||
      (qb.program_display_name && p.display_name === qb.program_display_name)
  );
  return match?.program_id ?? null;
};

// Label for one assigned bank: "<program> · <subject> · <subtopic>".
const assignedQBLabel = (qb) =>
  qb
    ? `${qb.program_display_name || qb.program_name || "—"} · ${qb.category || "—"} · ${qb.subcategory || "—"}`
    : "";

// Stable identity for a breakdown row so weights survive a filter re-fetch.
const rowIdentity = (r) =>
  `${r.session_key}__${r.marks}__${r.blooms_level}__${r.question_type}__${r.level}`;

// Flatten a working-section's picked items into [{ paper_question_id, quiz_id }] in
// display order (handles list / split / either-or shapes).
const flattenSectionQuizzes = (sec) => {
  const out = [];
  const splitMark = sec.splitMark || [];
  const secMarks = sec.marks;
  const push = (node) => {
    if (!node) return;
    if (node.subs)
      node.subs.forEach((sub, si) => sub && out.push({ paper_question_id: sub.paper_question_id, quiz_id: sub.id, mark: splitMark[si] ?? sub.marks ?? secMarks ?? 0 }));
    else if (node.id != null)
      out.push({ paper_question_id: node.paper_question_id, quiz_id: node.id, mark: secMarks ?? node.marks ?? 0 });
  };
  (sec.items || []).forEach((it) => {
    if (!it) return;
    if (sec.pattern === "either-or") { push(it.a); push(it.b); }
    else push(it);
  });
  return out;
};

const CreateQuestionPaper = () => {

  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const navigate = useNavigate();
  const location = useLocation();
  const { showSnackbar } = useSnackbar();

  const isEditMode = location.state?.edit || false;
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false); // full-screen overlay during create/update submit
  // paper_status from ViewQuestionPaper (DRAFT/READY/OPEN/COMPLETED...). Pool mappings
  // (section_subcategories) may only be updated while DRAFT — backend rejects otherwise.
  const [paperStatus, setPaperStatus] = useState(null);
  // "Complete anyway" flow: backend returns a warning when some assigned students are still
  // pending. We surface an Approve/Decline dialog, then re-submit with force_complete: true.
  const forceCompleteRef = useRef(false);
  const lastSubmitDataRef = useRef(null);
  const [completeWarning, setCompleteWarning] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [categories, setCategories] = useState([]);
  // Filter option lists — sourced from the Step 2 response `available_filters` (auto-narrowing).
  const [levelOptions, setLevelOptions] = useState([]);
  const [questionTypeOptions, setQuestionTypeOptions] = useState([]);
  const [marksOptions, setMarksOptions] = useState([]);
  const [bloomsOptions, setBloomsOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [isStatusOpen, setIsStatusOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [assignedQuestionBanks, setAssignedQuestionBanks] = useState([]);

  // weightages: grouped by subcategory ->
  //   { subcategory_id, subcategory_name, description, breakdown: [{ ...row, rowKey, weight }] }
  const [weightages, setWeightages] = useState([]);

  // ---- Template-driven question paper (FIXED / RANDOM) ----
  const [paperType, setPaperType] = useState("FIXED");      // FIXED | RANDOM
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [sections, setSections] = useState([]);             // FIXED: working sections + selected items
  const originalSectionsRef = useRef(null);                 // FIXED edit: snapshot of saved questions, for per-section sync on update
  const [activeIdx, setActiveIdx] = useState(0);
  const [previewPaper, setPreviewPaper] = useState(null);   // built parts[] paper for the preview
  const [previewShown, setPreviewShown] = useState(false);
  // RANDOM: per-section pool assignments — sectionPools[i] = [{ session_key, no_of_questions }]
  const [sectionPools, setSectionPools] = useState([]);
  // RANDOM edit: metadata for the saved pools, so their options always render even if a
  // fresh breakdown fetch doesn't reproduce them exactly.
  const [savedPoolMeta, setSavedPoolMeta] = useState([]);
  // Edit: the banks this paper was built from. ViewQuestionPaper returns `ids` as
  // [{ category_id, gen_id }] plus richer per-bank metadata in `subcategories`, keyed by
  // the same session_key — enough to restore the picker even for a bank the dropdown
  // list no longer returns. Bound once, by the effect below.
  const [savedBankIds, setSavedBankIds] = useState([]);
  const [savedBankMeta, setSavedBankMeta] = useState({});
  const banksBoundRef = useRef(false);
  const assignedBanksLoadedRef = useRef(false);
  // QuestionBankSessionsList returns banks from this institute plus ones shared by others.
  // Compare each row's institute_id against the logged-in institute so the dropdown can
  // flag which is which. Set at login (Login.js), read with + elsewhere in the app.
  const loginInstituteId = Number(localStorage.getItem("InstituteId")) || null;
  const isOwnInstituteBank = (qb) =>
    loginInstituteId != null && Number(qb?.institute_id) === loginInstituteId;

  // Both create and edit source questions from the assigned/mapped question banks — the
  // "Existing question bank" mode was removed. Edit starts with no banks picked (a saved
  // paper carries none to restore), so it stays scoped by the paper's own category_id
  // until the user picks some; see categoryScope().
  const chooseFromExistingQB = false;

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
    getValues,
    setError,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      program: null,
      primaryCategory: null,
      assignedQuestionBank: [],
      examType: null,
      cameraEnabled: null,
      timeDuration: null,
      academicYear: "",
      questionType: [],
      levelFilter: [],
      marksFilter: [],
      bloomsFilter: [],
      latestDate: null,
      scheduleTime: null,
      status: null,
      cancelReason: "",
      isEditMode: isEditMode,
      chooseFromExistingQB: isEditMode,
    },
    mode: "onChange",
  });

  const status = watch("status");
  const latestDate = watch("latestDate");
  const selectedProgram = watch("program");
  const selectedCategory = watch("primaryCategory");
  const timeDuration = watch("timeDuration");
  const academicYear = watch("academicYear");

  // A few live overrides for the question-paper template; the rest use sample defaults.
  // (mergeData in QuestionPaperTemplate ignores empty values, so unset fields fall back.)
  const templateData = {
    subjectName: selectedCategory?.name,
    subjectCode: selectedCategory?.code,
    department: selectedProgram?.display_name || selectedProgram?.program_name,
    academicYear: academicYear,
    time: timeDuration ? `${timeDuration} Minutes` : undefined,
  };

  // ---- Step 0: dropdown sources ----
  const fetchPrograms = async () => {
    try {
      // Role-aware programs for the logged-in user (no body; identified via JWT).
      const res = await api.post("GetAssignedProgramsDropdown", {});
      if (res?.data?.status === "success") {
        setPrograms(res?.data?.data || []);
      }
    } catch (error) {
      console.log("Error fetching programs:", error);
    }
  };

  // Saved paper templates for the chooser (POST /TemplatesDropdown).
  const fetchTemplates = async () => {
    try {
      const res = await api.post("TemplatesDropdown", {});
      setTemplates(res?.data?.data || []);
    } catch (error) {
      console.log("Error fetching templates:", error);
      setTemplates([]);
    }
  };

  // Bloom's dropdown uses the full canonical list (/GetBloomsLevels), not the auto-narrowing
  // available_filters set, so every Bloom's level stays selectable as a filter.
  const fetchBloomsLevels = async () => {
    try {
      const res = await api.post("/GetBloomsLevels", {});
      if (res?.data?.status === "success") {
        setBloomsOptions((res?.data?.data || []).map((b) => b.blooms_name).filter(Boolean));
      }
    } catch (error) {
      console.log("Error fetching Bloom's levels:", error);
    }
  };

  // Edit mode resolves the saved category by id (existing papers carry no program_id).
  const fetchAllCategories = async () => {
    try {
      const res = await api.post("ListCategoryNamesWithIDs");
      setCategories(res?.data?.categories || []);
    } catch (error) {
      console.log("Error :", error);
    }
  };

  const fetchStatusOptions = async () => {
    try {
      const res = await api.post("ListStatus");
      if (res?.data?.status === "success") {
        // status_names is [{ value, label, to_be_shown }]. ListStatus is the static
        // default (all to_be_shown:false); ViewQuestionPaper provides the computed set
        // and overrides this in edit mode. Only render options flagged to_be_shown.
        const opts = res?.data?.status_names || [];
        const hasFlag = opts.some((o) => "to_be_shown" in o);
        setStatusOptions(hasFlag ? opts.filter((o) => o.to_be_shown) : opts);
      }
    } catch (error) {
      console.log("Error fetching status options:", error);
    }
  };

  // Question bank sessions for the dropdown. Same endpoint the Question Bank list screen
  // uses; it is paginated (page is 0-based there), so ask for one large page since a
  // dropdown needs the whole set.
  const fetchAssignedQuestionBanks = async () => {
    try {
      const res = await api.post("/QuestionBankSessionsList", {
        page: 0,
        size: 500,
        search_key: "",
        category_id: null,
        subcategory_id: null,
        level: "",
        question_type: "",
      });
      if (res?.data?.status === "success") {
        setAssignedQuestionBanks(res?.data?.data || []);
      } else {
        showSnackbar("error", res?.data?.message || "Could not load question banks");
      }
    } catch (error) {
      console.log("Error fetching question bank sessions:", error);
      showSnackbar("error", error?.response?.data?.message || "Could not load question banks");
    } finally {
      // Settled either way — the restore effect can stop waiting for richer rows.
      assignedBanksLoadedRef.current = true;
    }
  };

  // ---- Step 1: program_id -> categories ----
  const fetchCategoriesByProgram = async (programId) => {
    if (!programId) {
      setCategories([]);
      return;
    }
    setLoading(true);
    try {
      // Role-aware subjects for the selected program (backend filters by JWT role).
      const res = await api.post("GetAssignedCategoriesDropdown", {
        program_id: programId,
      });
      // normalise to { id, name, code } so the Category Autocomplete stays consistent
      const cats = (res?.data?.data || []).map((c) => ({
        id: c.category_id,
        name: c.category_name,
        code: c.category_code,
      }));
      setCategories(cats);
    } catch (error) {
      console.log("Error fetching categories by program:", error);
    } finally {
      setLoading(false);
    }
  };

  // Distinct marks a RANDOM template needs: each part's marks, or its split_mark values.
  const templateRequiredMarks = (t) => {
    const set = new Set();
    (t?.parts || []).forEach((p) => {
      const sm = p.split_mark || p.splitMark || [];
      if (Array.isArray(sm) && sm.length) sm.forEach((m) => set.add(Number(m)));
      else if (p.marks != null) set.add(Number(p.marks));
    });
    return [...set];
  };

  // The selected banks as ids[] = [{ category_id, gen_id }]; empty in existing-bank mode.
  const activeBankIds = () =>
    chooseFromExistingQB ? [] : assignedBankIds(getValues("assignedQuestionBank"));

  // Subject scope for a request body. Mapped/Assigned mode sends ids[] alone — each entry
  // carries its own category_id, so a top-level one would be redundant (and wrong when the
  // banks span subjects). Existing-bank mode sends the chosen subject.
  const categoryScope = (fallbackId) => {
    const bankIds = activeBankIds();
    if (bankIds.length) return { ids: bankIds };
    // A MULTI_CATEGORY paper has no single subject — omit the key rather than send null.
    return fallbackId != null ? { category_id: fallbackId } : {};
  };

  // program_id for a request body — from the Program field in existing-bank mode, or from
  // the selected banks in mapped mode (QuestionBankSessionsList may carry it). Returns null
  // rather than undefined: JSON.stringify drops undefined keys, so an unresolved program_id
  // would vanish from the body and the backend would report it as missing.
  const activeProgramId = () => {
    const banks = getValues("assignedQuestionBank") || [];
    for (const qb of banks) {
      const id = resolveProgramId(qb, programs);
      if (id != null) return id;
    }
    if (banks.length) {
      console.log("Could not resolve program_id for selected banks:", banks, "programs:", programs);
    }
    // No banks picked (or none resolved): fall back to the program field, which edit mode
    // fills from the saved paper's program_id.
    return getValues("program")?.program_id ?? null;
  };

  // The { program_id, display_name, … } shape the Program Autocomplete produces, so the
  // preview header's department resolves in mapped mode.
  const programFromAssignedQBs = (qbs) => {
    const qb = (qbs || [])[0];
    const id = resolveProgramId(qb, programs);
    return id != null
      ? {
        program_id: id,
        display_name: qb.program_display_name || "",
        program_name: qb.program_name || "",
        program_code: qb.program_code || "",
      }
      : null;
  };

  // ---- Step 2: category_id (+ filters) -> subcategory breakdown ----
  // `overrides` lets a caller pass the template / paper type it has just resolved. Callers that
  // run in the same tick as setSelectedTemplate / setPaperType would otherwise read the previous
  // values from the closure, silently skipping the RANDOM marks narrowing below.
  const fetchBreakdown = async (category, filters = {}, preserve = false, quiet = false, overrides = {}) => {
    // Needs a real subject or at least one bank to scope by. A MULTI_CATEGORY paper has
    // category.id === null, which would otherwise post an unscoped body.
    if (category?.id == null && !activeBankIds().length) {
      setWeightages([]);
      return;
    }
    const { level = [], questionType = [], marks = [], blooms = [] } = filters;

    // RANDOM (template-driven): when no explicit marks filter is set, fetch exactly the
    // marks the template needs (CHANGE 1) so each split-mark dropdown gets its real pools.
    let effMarks = marks;
    const tpl = "template" in overrides ? overrides.template : selectedTemplate;
    const isRandom = "isRandom" in overrides ? overrides.isRandom : paperType === "RANDOM";
    if ((!effMarks || !effMarks.length) && isRandom && tpl) {
      effMarks = templateRequiredMarks(tpl);
    }

    if (!quiet) setLoading(true);
    try {
      const body = { ...categoryScope(category.id) };
      const programId = activeProgramId();
      if (programId != null) body.program_id = programId;
      if (level.length) body.level = level;
      if (questionType.length) body.question_type = questionType;
      if (effMarks.length) body.marks = effMarks;
      if (blooms.length) body.blooms_levels = blooms;

      const res = await api.post("CreateQuestionPaper", body);
      const data = res?.data?.data || [];

      const groups = data.map((sub) => ({
        subcategory_id: sub.subcategory_id,
        subcategory_name: sub.subcategory_name,
        description: sub.description || "",
        breakdown: (sub.breakdown || []).map((row, idx) => ({
          ...row,
          subcategory_id: sub.subcategory_id,
          subcategory_name: sub.subcategory_name,
          rowKey: `${sub.subcategory_id}_${idx}`,
          weight: 0,
        })),
      }));

      // Level / question type / marks auto-narrow from the backend; Bloom's keeps its full list.
      const af = res?.data?.available_filters || {};
      setLevelOptions(af.levels || []);
      setQuestionTypeOptions(af.question_types || []);
      setMarksOptions(af.marks || []);

      setWeightages((prev) => {
        if (!preserve) return groups;
        const prevWeights = {};
        prev.forEach((g) => g.breakdown.forEach((r) => { prevWeights[rowIdentity(r)] = r.weight; }));
        return groups.map((g) => ({
          ...g,
          breakdown: g.breakdown.map((r) => ({
            ...r,
            weight: prevWeights[rowIdentity(r)] ?? 0,
          })),
        }));
      });
    } catch (error) {
      console.log("Error fetching breakdown:", error);
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrograms();
    fetchBloomsLevels();
    fetchStatusOptions();
    fetchTemplates();
    fetchAssignedQuestionBanks();
    if (isEditMode) fetchAllCategories();
  }, []);

  // The chooser is bound by object, and in edit the template is rebuilt from the saved
  // paper (ViewQuestionPaper gives only template_id + template_name) before
  // /TemplatesDropdown has necessarily answered. Once the list arrives, adopt the
  // canonical name/format for that id — keeping the reconstructed parts, which carry the
  // saved paper_section_ids the edit flow needs and the dropdown option does not have.
  useEffect(() => {
    if (!templates.length) return;
    setSelectedTemplate((prev) => {
      if (!prev?.template_id) return prev;
      const match = templates.find((t) => t.template_id === prev.template_id);
      if (!match) return prev;
      const name = match.template_name || match.name || prev.template_name;
      if (name === prev.template_name) return prev; // already bound — don't loop
      return { ...prev, template_name: name, format: prev.format || match.format };
    });
  }, [templates]);

  // Edit: restore the assigned-bank picker from the saved `ids`. Runs exactly once
  // (banksBoundRef) so that de-selecting a bank afterwards is not undone on the next
  // render. It waits for QuestionBankSessionsList so the picked rows carry full labels,
  // but does not wait forever — if that call comes back empty the saved metadata is
  // enough to show the bank and send it back in the payload.
  useEffect(() => {
    if (!isEditMode || banksBoundRef.current || !savedBankIds.length) return;
    if (!assignedQuestionBanks.length && !assignedBanksLoadedRef.current) return;

    const restored = savedBankIds.map(({ gen_id, category_id }) => {
      const fromList = assignedQuestionBanks.find((qb) => qb.session_key === gen_id);
      if (fromList) return fromList;
      // Not in the dropdown list — rebuild just enough to label it and re-send it.
      const meta = savedBankMeta[gen_id] || {};
      return {
        session_key: gen_id,
        category_id,
        category: meta.category || "",
        subcategory: meta.subcategory_name || "",
        level: meta.level || "",
        question_type: meta.question_type || "",
        total_questions: meta.available_questions,
      };
    });

    banksBoundRef.current = true;
    setValue("assignedQuestionBank", restored);
    // Populate the Level / Question Type / Marks filter options for these banks.
    fetchBreakdown(getValues("primaryCategory"), {}, false, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignedQuestionBanks, savedBankIds, savedBankMeta, isEditMode]);

  // Header overrides for the built paper, from the live form values.
  const previewHeaderForm = () => ({
    subjectName: selectedCategory?.name,
    subjectCode: selectedCategory?.code,
    department: selectedProgram?.display_name || selectedProgram?.program_name,
    academicYear: academicYear,
    time: timeDuration ? `${timeDuration} Minutes` : undefined,
  });

  const handlePaperTypeChange = (type) => {
    setPaperType(type);
    // Re-seed one (empty) pool list per section of the template that is already chosen —
    // blanking to [] left the RANDOM pool UI rendering against an array with no slots.
    setSectionPools((selectedTemplate?.parts || []).map(() => []));
    setSavedPoolMeta([]);
    if (type === "FIXED" && selectedTemplate) {
      // Restore the live paper preview, same as choosing the template while on FIXED.
      // Blanking it here left previewShown false with nothing to turn it back on, so
      // questions added below never appeared in the preview.
      setPreviewPaper(buildPaperFromSections(sections, buildHeader(previewHeaderForm(), selectedTemplate)));
      setPreviewShown(true);
      return;
    }
    setPreviewShown(false);
    setPreviewPaper(null);
    // Switching to RANDOM with a template already picked: reload the pools narrowed to
    // the marks that template needs, same as choosing the template while on RANDOM.
    if (type === "RANDOM" && selectedTemplate) {
      const cat = getValues("primaryCategory");
      if (cat) fetchBreakdown(cat, {}, false, false, { template: selectedTemplate, isRandom: true });
    }
  };

  const handleTemplateChange = async (t) => {
    // List responses may be summary-only (no parts) — fetch the full template.
    let full = t;
    if (t && !(t.parts && t.parts.length) && t.template_id) {
      try {
        const res = await api.post("GetPaperTemplate", { template_id: t.template_id });
        if (res?.data?.data) full = res.data.data;
      } catch (e) {
        console.log("GetPaperTemplate error:", e);
      }
    }
    setSelectedTemplate(full);
    const secs = full ? sectionsFromTemplate(full) : [];
    setSections(secs);
    setActiveIdx(0);
    // RANDOM: start one empty pool row per section so the admin can assign pools.
    setSectionPools((full?.parts || []).map(() => []));
    // FIXED: show the question paper immediately; slots fill in as questions are selected.
    if (full && paperType === "FIXED") {
      setPreviewPaper(buildPaperFromSections(secs, buildHeader(previewHeaderForm(), full)));
      setPreviewShown(true);
    } else {
      setPreviewShown(false);
      setPreviewPaper(null);
      // RANDOM: reload the pools narrowed to the marks this template needs, so every
      // split-mark sub-section gets its real options (works whichever order the template
      // and the subject / assigned banks were chosen in).
      const cat = getValues("primaryCategory");
      if (full && cat) fetchBreakdown(cat, {}, false, false, { template: full, isRandom: true });
    }
  };

  // Build the /PreviewRandomPaper + /CreateQuestionPaper section_pools list from assignments.
  const buildSectionPoolsPayload = () => {
    const out = [];
    sectionPools.forEach((rows, si) =>
      (rows || []).forEach((p) => {
        if (p.session_key && Number(p.no_of_questions) > 0) {
          out.push({
            section_index: si,
            session_key: p.session_key,
            no_of_questions: Number(p.no_of_questions),
            marks: p.marks ?? null,
            blooms_level: p.blooms_level ?? null,
            available_questions: p.available_questions || 0,
          });
        }
      })
    );
    return out;
  };

  const handleShowPreview = async () => {
    if (!selectedTemplate) return;
    const header = buildHeader(previewHeaderForm(), selectedTemplate);
    if (paperType === "RANDOM") {
      // program_id is required by the backend. Catch it here rather than sending a body
      // that is guaranteed to fail validation.
      const programId = activeProgramId();
      if (programId == null) {
        showSnackbar(
          "error",
          chooseFromExistingQB
            ? `Select a ${getLabel('PROGRAMS')} before previewing the paper.`
            : "The selected question bank has no program mapped — pick another bank or set its program."
        );
        return;
      }
      // Stateless live random draw via /PreviewRandomPaper (repeatable, nothing saved).
      setLoading(true);
      try {
        const res = await api.post("PreviewRandomPaper", {
          template_id: selectedTemplate.template_id,
          category_id: selectedCategory?.id ?? null,
          program_id: programId,
          academic_year: academicYear || "",
          exam_date: latestDate ? dayjs(latestDate).format("YYYY-MM-DD") : null,
          time_limit: timeDuration ? String(timeDuration) : "",
          section_pools: buildSectionPoolsPayload(),
        });
        if (res?.data?.status === "success") {
          setPreviewPaper(buildPaperFromPreview(res.data.data, previewHeaderForm()));
          setPreviewShown(true);
        } else {
          showSnackbar("error", res?.data?.message || "Could not generate preview");
        }
      } catch (e) {
        showSnackbar("error", e?.response?.data?.message || "Could not generate preview");
      } finally {
        setLoading(false);
      }
      return;
    }
    setPreviewPaper(buildPaperFromSections(sections, header));
    setPreviewShown(true);
  };

  // Keep the FIXED preview in sync as the teacher adds/removes questions.
  useEffect(() => {
    if (previewShown && paperType === "FIXED" && selectedTemplate) {
      setPreviewPaper(buildPaperFromSections(sections, buildHeader(previewHeaderForm(), selectedTemplate)));
    }
    // previewShown / paperType are deps too: sections may have changed while the preview was
    // hidden, and without them the paper would stay stale after it is shown again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, previewShown, paperType, selectedTemplate]);

  // Keep the preview header live as General Information (program/category/
  // academic year/time) is edited — patch the header without rebuilding questions.
  useEffect(() => {
    if (!previewShown) return;
    setPreviewPaper((prev) =>
      prev ? { ...prev, ...buildHeader(previewHeaderForm(), selectedTemplate) } : prev
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategory, selectedProgram, academicYear, timeDuration]);

  useEffect(() => {
    if (isEditMode && categories.length > 0) {
      fetchQuestionPaperData();
    }
  }, [isEditMode, categories]);

  useEffect(() => {
    // Clear status error when status is OPEN and schedule date is today
    if (status && status.toUpperCase() === "OPEN" && latestDate) {
      const scheduleDate = dayjs(latestDate).startOf("day");
      const today = dayjs().startOf("day");
      if (scheduleDate.isSame(today) && errors.status) {
        setError("status", null);
      }
    }
  }, [status, latestDate]);

  const fetchQuestionPaperData = async () => {
    setLoading(true);
    try {
      const res = await api.post("ViewQuestionPaper", {
        assessment_id: location.state?.assessment_id,
      });

      if (res?.data?.status === "success") {
        const data = res?.data?.data;
        // A paper built from banks spanning subjects comes back with category_id: null and
        // category_name: "MULTI_CATEGORY". Fall back to whatever the response names so the
        // Subject field still shows something; a null id simply means "no single subject",
        // and the assigned-bank picker below is how such a paper is re-scoped.
        const categoryObj =
          categories.find((cat) => cat.id === data.category_id) ||
          (data.category_id != null || data.category_name
            ? { id: data.category_id ?? null, name: data.category_name || "", code: "" }
            : null);
        const examType = data.test_type === 1 ? "Self Assessment" : "Proctored Assessments";
        const cameraEnabled = data.camera_assigned_status === "YES" ? "Enabled" : "Disabled";
        const questionTypes = data.question_type ? data.question_type.split(", ").map((q) => q.trim()) : [];

        // Saved subcategories are flat — wrap each as a single-row group so the same renderer works.
        const groups = (data.subcategories || []).map((sub, gi) => ({
          subcategory_id: sub.subcategory_id,
          subcategory_name: sub.subcategory_name,
          description: sub.description || "",
          breakdown: [
            {
              ...sub,
              subcategory_id: sub.subcategory_id,
              subcategory_name: sub.subcategory_name,
              rowKey: `${sub.subcategory_id}_${gi}_0`,
              weight: sub.no_of_questions !== undefined ? sub.no_of_questions : (sub.percentage || 0),
            },
          ],
        }));

        // The banks this paper draws from, and the metadata to label them. Restored into
        // the picker by the effect below, once the dropdown list has settled.
        setSavedBankIds(Array.isArray(data.ids) ? data.ids.filter((b) => b?.gen_id) : []);
        setSavedBankMeta(
          Object.fromEntries(
            (data.subcategories || [])
              .filter((s) => s.session_key)
              .map((s) => [s.session_key, s])
          )
        );

        setValue("primaryCategory", categoryObj || null);
        // program_id drives the update payload; the header carries its display text.
        // Match the programs dropdown first so the label is the canonical one.
        if (data.program_id != null) {
          const savedProgram = programs.find((p) => p.program_id === data.program_id);
          setValue(
            "program",
            savedProgram || {
              program_id: data.program_id,
              display_name: data.header?.department || "",
              program_name: data.header?.department || "",
              program_code: data.header?.program_code || "",
            }
          );
        }
        setValue("levelFilter", data.level ? [data.level] : []);
        setValue("examType", examType);
        setValue("cameraEnabled", cameraEnabled);
        // time_limit may carry a unit ("30 Minutes") or be a bare number — bind just the number.
        setValue("timeDuration", parseFloat(String(data.time_limit)) || 0);
        setValue("questionType", questionTypes);
        setValue("academicYear", data.header?.academic_year || data.academic_year || "");
        setValue("latestDate", data.scheduled_date ? dayjs(data.scheduled_date) : null);
        // scheduled_time is a 24h time string ("HH:mm:ss") — anchor to a date for the picker.
        setValue("scheduleTime", data.scheduled_time ? dayjs(`2000-01-01T${data.scheduled_time}`) : null);
        setValue("status", data.status || "");

        // status_options reflects the allowed forward transitions for the current status
        // (prevents backward moves, e.g. OPEN → SCHEDULED). Only show options flagged
        // to_be_shown; this is authoritative over the static ListStatus defaults.
        if (Array.isArray(data.status_options)) {
          setStatusOptions(data.status_options.filter((o) => o.to_be_shown));
        }

        // FIXED papers are edited with the same template + question-picking setup as create.
        if (data.paper_type === "FIXED" && Array.isArray(data.sections) && data.sections.length) {
          setPaperType("FIXED");
          const editSections = sectionsFromSavedPaper(data.sections);
          // Snapshot the saved questions per section (flat, in order) to diff against on update.
          originalSectionsRef.current = editSections.map((s) => ({
            paper_section_id: s.paper_section_id,
            questions: flattenSectionQuizzes(s),
          }));
          // Reconstruct a lightweight template object so the chooser summary + preview render.
          const parts = data.sections.map((s) => ({
            key: `sec_${s.paper_section_id}`,
            title: s.section_name,
            pattern: s.pattern,
            count: editSections.find((e) => e.paper_section_id === s.paper_section_id)?.count || 0,
            marks: editSections.find((e) => e.paper_section_id === s.paper_section_id)?.marks || 0,
            start: s.start_number,
            split_mark: s.split_mark || [],
            instructions: s.instructions || [],
            choice: s.choice || null,
          }));
          setSelectedTemplate({
            template_id: data.template_id,
            template_name: data.template_name,
            format: data.paper_type,
            header_defaults: data.header || {},
            parts,
          });
          setSections(editSections);
          setActiveIdx(0);
          setPreviewShown(true); // show the live paper preview (rebuilt from sections)
          // Populate Level/Type/Marks filter options. FIXED needs no marks narrowing, and
          // setPaperType has not committed yet, so state it explicitly.
          if (categoryObj?.id != null) fetchBreakdown(categoryObj, {}, false, false, { isRandom: false });
        } else {
          setPaperType("RANDOM");
          // New RANDOM papers carry per-section pools[] — reconstruct the template + assignments
          // so the same per-section pool UI (as create) edits them.
          if (Array.isArray(data.sections) && data.sections.some((s) => Array.isArray(s.pools))) {
            const parts = data.sections.map((s) => ({
              key: `sec_${s.paper_section_id}`,
              partkey: s.paper_section_id,
              title: s.section_name,
              pattern: s.pattern,
              count: s.no_of_questions,
              slot_count: s.no_of_questions, // saved slot count drives validation directly
              marks: s.marks_per_question,
              start: s.start_number,
              split_mark: s.split_mark || [],
              choice: s.choice || null,
              instructions: s.instructions || [],
            }));
            const editTemplate = {
              template_id: data.template_id,
              template_name: data.template_name,
              format: "RANDOM",
              header_defaults: data.header || {},
              parts,
            };
            setSelectedTemplate(editTemplate);
            setSectionPools(
              data.sections.map((s) =>
                (s.pools || []).map((p) => ({
                  session_key: p.session_key,
                  marks: p.marks != null ? p.marks : s.marks_per_question,
                  // group_mark = which split-mark sub-section this pool belongs to.
                  group_mark: p.marks != null ? p.marks : s.marks_per_question,
                  blooms_level: p.blooms_level ?? null,
                  available_questions: p.available_questions || 0,
                  no_of_questions: p.no_of_questions,
                }))
              )
            );
            // Keep the saved pools' metadata so their dropdown options/labels/available
            // always render (independent of the breakdown fetch below).
            setSavedPoolMeta(
              (data.sections || []).flatMap((s) =>
                (s.pools || []).map((p) => ({
                  session_key: p.session_key,
                  subcategory_name: p.subcategory_name,
                  level: p.level,
                  question_type: p.question_type,
                  marks: p.marks != null ? p.marks : s.marks_per_question,
                  blooms_level: p.blooms_level ?? null,
                  available_questions: p.available_questions || 0,
                }))
              )
            );
            // Refresh available counts, narrowed to the marks the saved template needs.
            // setSelectedTemplate / setPaperType have not committed yet, so both are passed
            // explicitly — otherwise the narrowing is skipped and split-mark sub-sections
            // come back with no options.
            if (categoryObj?.id != null) {
              fetchBreakdown(categoryObj, {}, false, false, { template: editTemplate, isRandom: true });
            }
          } else {
            setWeightages(groups); // legacy weightage papers
          }
        }
        setIsStatusOpen(data.status === "OPEN");
        setPaperStatus(data.paper_status || null);

        if (data.status && data.status.toUpperCase() === "OPEN" && data.scheduled_date) {
          const scheduleDate = dayjs(data.scheduled_date).startOf("day");
          const today = dayjs().startOf("day");
          if (!scheduleDate.isSame(today)) {
            setError("status", {
              type: "manual",
              message: "Schedule date should be today when status is OPEN",
            });
          } else if (errors.status) {
            setError("status", null);
          }
        }
      }
    } catch (error) {
      console.log("Error fetching question paper data:", error);
    } finally {
      setLoading(false);
    }
  };

  const allRows = weightages.flatMap((g) => g.breakdown);
  const totalQuestionsCount = allRows.reduce((sum, item) => sum + (Number(item.weight) || 0), 0);
  // Questions picked inline via the section browser (FIXED template flow).
  const pickedQuestionsCount = sections.reduce((sum, sec) => sum + (sec.items?.length || 0), 0);

  // ---- RANDOM: pool options + per-section assignment helpers ----
  // A pool is identified by session_key + marks + blooms_level (the backend narrows the draw
  // to that exact combination). Blooms is normalised (breakdown gives "L1-Remember", saved
  // papers give "Remember") so the same pool matches across create / edit.
  const bloomName = (b) => (b ? String(b).replace(/^L\d+-/, "") : "");
  const poolId = (r) => `${r.session_key}|${r.marks ?? ""}|${bloomName(r.blooms_level)}`;
  const toPoolOption = (r) => ({
    id: poolId(r),
    session_key: r.session_key,
    subcategory_name: r.subcategory_name,
    level: r.level,
    question_type: r.question_type,
    marks: r.marks,
    blooms_level: r.blooms_level,
    // What this exact pool can still supply. The breakdown reports remaining_questions
    // (available minus what other papers already hold) alongside available_questions —
    // prefer it, since it is the number the backend validates the draw against and
    // rejects with "Insufficient questions for pool ... Requested: N, Available: M".
    available_questions: Number(r.remaining_questions ?? r.available_questions) || 0,
    label: `${r.subcategory_name || "—"} · ${r.level || "—"} · ${r.question_type || "—"} · ${r.blooms_level || "Untagged"}`,
  });
  const poolOptions = (() => {
    const base = allRows.filter((r) => r.session_key).map(toPoolOption);
    const seen = new Set(base.map((p) => p.id));
    // Merge saved pools (edit) the fresh breakdown didn't return, so they stay selectable.
    const extra = (savedPoolMeta || []).filter((p) => p.session_key && !seen.has(poolId(p))).map(toPoolOption);
    return [...base, ...extra];
  })();
  const poolById = Object.fromEntries(poolOptions.map((p) => [p.id, p]));
  const rowId = (row) => poolId(row);
  // Resolve a stored row to its option (fallback to the row itself if the breakdown no
  // longer lists it — e.g. on edit before the refetch resolves).
  const rowOption = (row) => poolById[rowId(row)] || (row.session_key ? toPoolOption(row) : null);

  const sectionSlots = (selectedTemplate?.parts || []).map((p) => (p.slot_count != null ? p.slot_count : slotCount(p)));
  const poolSum = (si) => (sectionPools[si] || []).reduce((t, p) => t + (Number(p.no_of_questions) || 0), 0);

  // A split part (split_mark [6,7]) becomes one pool sub-section per distinct mark.
  // Total slots = slotCount(part) (e.g. either-or count 5 split [6,7] → 2×2×5 = 20),
  // split evenly across the split values (20 / 2 = 10 each → marks=6:10, marks=7:10).
  // Derived from the part's total slots (not count) so it's correct for both create
  // (template count) and edit (saved slot_count). Duplicate split values are merged.
  const partGroups = (part) => {
    if (!part) return [];
    const marks = Array.isArray(part.split_mark) && part.split_mark.length ? part.split_mark : [part.marks];
    const total = part.slot_count != null ? part.slot_count : slotCount(part);
    const perOccurrence = marks.length ? total / marks.length : 0;
    const map = new Map();
    marks.forEach((m) => map.set(m, (map.get(m) || 0) + perOccurrence));
    return [...map.entries()].map(([mark, slots]) => ({ mark, slots }));
  };
  const groupRowsWithIdx = (si, mark) =>
    (sectionPools[si] || [])
      .map((row, ri) => ({ row, ri }))
      .filter(({ row }) => Number(row.group_mark) === Number(mark));
  const groupSum = (si, mark) =>
    groupRowsWithIdx(si, mark).reduce((t, { row }) => t + (Number(row.no_of_questions) || 0), 0);
  // How many questions one pool row may claim. A pool is one (session_key, marks,
  // blooms_level) combination and the breakdown reports its count for exactly that
  // combination — rows sharing a session_key at different Bloom's levels draw from
  // separate question sets, so each row is capped by its own pool alone.
  const rowAvailable = (si, ri, row) => Number(rowOption(row)?.available_questions) || 0;
  const groupOk = (si, g) => {
    const rows = groupRowsWithIdx(si, g.mark);
    if (rows.length === 0) return false;
    if (groupSum(si, g.mark) !== g.slots) return false;
    return rows.every(({ row, ri }) => {
      const opt = rowOption(row);
      return row.session_key && opt && Number(row.no_of_questions) <= rowAvailable(si, ri, row);
    });
  };
  const sectionPoolOk = (si) => {
    const part = (selectedTemplate?.parts || [])[si];
    const groups = partGroups(part);
    if (!groups.length) return false;
    return groups.every((g) => groupOk(si, g));
  };
  const randomValid =
    paperType === "RANDOM" &&
    (selectedTemplate?.parts || []).length > 0 &&
    (selectedTemplate.parts || []).every((_, si) => sectionPoolOk(si));

  // Pad to si before writing: a plain map() over a shorter array silently drops the write,
  // which is how "Add pool" could no-op when sectionPools was out of sync with the template.
  const setPools = (si, rows) =>
    setSectionPools((prev) => {
      const next = [...prev];
      while (next.length <= si) next.push([]);
      next[si] = rows;
      return next;
    });
  // Add a blank pool row to a specific split-mark sub-section. The row is tagged
  // with group_mark so it renders under that section and only offers that mark's pools.
  const addPoolRow = (si, mark) => {
    if (poolOptions.length === 0) {
      showSnackbar(
        "error",
        chooseFromExistingQB
          ? `Select a ${getLabel('PROGRAMS')} and ${getLabel('SUBJECT')} above to load the available pools.`
          : `Select one or more assigned ${getLabel('QUESTION_BANK')} above to load the available pools.`
      );
      return;
    }
    if (!poolOptions.some((o) => Number(o.marks) === Number(mark))) {
      showSnackbar("error", `No ${mark}-mark pools available for the selected ${getLabel('SUBJECT')}/filters.`);
      return;
    }
    setPools(si, [
      ...(sectionPools[si] || []),
      { session_key: "", group_mark: mark, marks: mark, blooms_level: null, available_questions: 0, no_of_questions: 0 },
    ]);
  };
  const removePoolRow = (si, ri) =>
    setPools(si, (sectionPools[si] || []).filter((_, i) => i !== ri));
  const setPoolOption = (si, ri, o) =>
    setPools(
      si,
      (sectionPools[si] || []).map((p, i) =>
        i === ri
          ? {
            ...p,
            session_key: o?.session_key || "",
            // Keep full identity so the label still renders if a filter later
            // removes this pool from the freshly fetched options.
            subcategory_name: o?.subcategory_name ?? "",
            level: o?.level ?? "",
            question_type: o?.question_type ?? "",
            marks: o?.marks ?? null,
            blooms_level: o?.blooms_level ?? null,
            available_questions: o?.available_questions || 0,
          }
          : p
      )
    );
  const setPoolCount = (si, ri, val) =>
    setPools(si, (sectionPools[si] || []).map((p, i) => (i === ri ? { ...p, no_of_questions: val === "" ? "" : Math.max(0, Number(val)) } : p)));

  const handleWeightChange = (rowKey, value) => {
    setWeightages((prev) =>
      prev.map((g) => ({
        ...g,
        breakdown: g.breakdown.map((r) =>
          r.rowKey === rowKey ? { ...r, weight: value === "" ? "" : Number(value) } : r
        ),
      }))
    );
  };

  // Re-run the drill-down when a filter changes, preserving already-entered counts.
  const refetchWithFilters = (overrides = {}) => {
    const category = getValues("primaryCategory");
    if (!category) return;
    fetchBreakdown(
      category,
      {
        level: getValues("levelFilter") || [],
        questionType: getValues("questionType") || [],
        marks: getValues("marksFilter") || [],
        blooms: getValues("bloomsFilter") || [],
        ...overrides,
      },
      true
    );
  };

  // Filters narrow the pools a RANDOM paper draws from just as they narrow the questions a
  // FIXED paper browses, in both create and edit — but there has to be something to narrow.
  // A MULTI_CATEGORY paper has no subject id, so it needs banks picked first.
  const filtersDisabled = selectedCategory?.id == null && activeBankIds().length === 0;

  // Backend asks for confirmation (some assigned students still pending) before completing.
  const isCompleteWarning = (res) =>
    res?.data?.status === "warning" && res?.data?.requires_confirmation;

  // Approve → re-submit the same request with force_complete: true. Decline → cancel.
  const handleApproveComplete = () => {
    forceCompleteRef.current = true;
    setCompleteWarning(null);
    if (lastSubmitDataRef.current) onSubmit(lastSubmitDataRef.current);
  };
  const handleDeclineComplete = () => {
    forceCompleteRef.current = false;
    setCompleteWarning(null);
  };

  const onSubmit = async (data) => {
    lastSubmitDataRef.current = data;
    if (paperType === "RANDOM" && selectedTemplate) {
      if (!randomValid) {
        showSnackbar("error", "Assign pools to every section — each section's counts must equal its required slots.");
        return;
      }
    } else if (totalQuestionsCount === 0 && pickedQuestionsCount === 0) {
      showSnackbar("error", "Please select at least one question");
      return;
    }

    const pickedRows = allRows.filter((r) => Number(r.weight) > 0);

    // Save needs a single paper-level `level`: the dominant level by question count.
    const levelTotals = {};
    pickedRows.forEach((r) => {
      if (r.level) levelTotals[r.level] = (levelTotals[r.level] || 0) + Number(r.weight);
    });
    const paperLevel =
      Object.keys(levelTotals).sort((a, b) => levelTotals[b] - levelTotals[a])[0] ||
      (getValues("levelFilter") || [])[0] ||
      "";

    const paperQuestionTypes = [...new Set(pickedRows.map((r) => r.question_type).filter(Boolean))];

    if (isEditMode) {
      if (!data.status) {
        showSnackbar("error", "Status is required");
        return;
      }
      if (data.status && data.status.toUpperCase() === "OPEN" && data.latestDate) {
        const scheduleDate = dayjs(data.latestDate).startOf("day");
        const today = dayjs().startOf("day");
        if (!scheduleDate.isSame(today)) {
          setError("status", {
            type: "manual",
            message: "Schedule date should be today when status is OPEN",
          });
          showSnackbar("error", "Schedule date should be today when status is OPEN");
          return;
        }
      }

      // FIXED papers: UpdateQuestionPaper handles metadata; question changes go through the
      // per-section endpoints (Browse + Add / Remove / Reorder), same as the create flow.
      if (paperType === "FIXED") {
        setLoading(true);
        setSubmitting(true);
        try {
          const assessmentId = location.state?.assessment_id;
          const metaRes = await api.post("UpdateQuestionPaper", {
            assessment_id: assessmentId,
            // null for a MULTI_CATEGORY paper — 0 would claim a subject it does not have.
            category_id: data.primaryCategory?.id ?? null,
            test_type: data.examType === "Self Assessment" ? 1 : 2,
            time_limit: data.timeDuration ? `${data.timeDuration}` : "",
            academic_year: data.academicYear || "",
            camera_assigned_status: data.cameraEnabled === "Enabled" ? "YES" : "NO",
            status: data.status?.toUpperCase(),
            // scheduled_date (+ schedule_for_later / scheduled_time) — single assessment date.
            ...buildSchedulePayload(data),
            ...(forceCompleteRef.current ? { force_complete: true } : {}),
            remarks: data.status === "CANCELLED" ? data.cancelReason || cancelReason : null,
          });
          // Pending-students warning → ask the user to approve completing anyway.
          if (isCompleteWarning(metaRes)) {
            setCompleteWarning(metaRes.data);
            setLoading(false);
            setSubmitting(false);
            return;
          }
          if (metaRes?.data?.status !== "success") {
            showSnackbar("error", metaRes?.data?.message || `Failed to update ${getLabel('QUESTION_PAPER')}`);
            setLoading(false);
            return;
          }

          // Sync each section against the loaded snapshot: remove deleted, add new, reorder.
          for (const sec of sections) {
            if (!sec.paper_section_id) continue;
            const orig = (originalSectionsRef.current || []).find((o) => o.paper_section_id === sec.paper_section_id);
            const origList = orig?.questions || [];
            const currList = flattenSectionQuizzes(sec);
            const currQuizSet = new Set(currList.map((q) => q.quiz_id));

            for (const r of origList) {
              if (r.paper_question_id && !currQuizSet.has(r.quiz_id)) {
                await api
                  .post("RemoveQuestionFromSection", { assessment_id: assessmentId, paper_question_id: r.paper_question_id })
                  .catch((e) => console.log("RemoveQuestionFromSection error:", e));
              }
            }
            const addEntries = currList.filter((q) => !q.paper_question_id);
            const addQuizIds = addEntries.map((q) => q.quiz_id);
            const newIdByQuiz = {};
            if (addQuizIds.length) {
              const addRes = await api
                .post("AddQuestionsToSection", {
                  assessment_id: assessmentId,
                  paper_section_id: sec.paper_section_id,
                  quiz_ids: addQuizIds,
                  marks_override: addEntries.map((q) => q.mark ?? 0),
                })
                .catch((e) => { console.log("AddQuestionsToSection error:", e); return null; });
              (addRes?.data?.added || []).forEach((a) => { newIdByQuiz[a.quiz_id] = a.paper_question_id; });
            }
            const orderedIds = currList.map((q) => q.paper_question_id || newIdByQuiz[q.quiz_id]).filter(Boolean);
            if (orderedIds.length) {
              await api
                .post("ReorderQuestionsInSection", { assessment_id: assessmentId, paper_section_id: sec.paper_section_id, ordered_paper_question_ids: orderedIds })
                .catch((e) => console.log("ReorderQuestionsInSection error:", e));
            }
          }

          showSnackbar("success", `${getLabel('QUESTION_PAPER')} updated successfully!`);
          navigate(-1);
        } catch (error) {
          console.log("Error:", error);
          showSnackbar("error", error?.response?.data?.message || `Failed to update ${getLabel('QUESTION_PAPER')}`);
        } finally {
          setLoading(false);
          setSubmitting(false);
        }
        return;
      }

      setLoading(true);
      setSubmitting(true);
      try {
        const requestBody = {
          assessment_id: location.state?.assessment_id,
          ...categoryScope(data.primaryCategory?.id || 0),
          category_name: data.primaryCategory?.name || "",
          level: paperLevel,
          test_type: data.examType === "Self Assessment" ? 1 : 2,
          no_of_questions: totalQuestionsCount,
          time_limit: String(data.timeDuration || ""),
          camera_assigned_status: data.cameraEnabled === "Enabled" ? "YES" : "NO",
          status: data.status?.toUpperCase(),
          // scheduled_date (+ schedule_for_later / scheduled_time) — single assessment date.
          ...buildSchedulePayload(data),
          ...(forceCompleteRef.current ? { force_complete: true } : {}),
          question_type: paperQuestionTypes,
          remarks: data.status === "CANCELLED" ? data.cancelReason || cancelReason : null,
        };

        if (selectedTemplate) {
          // New RANDOM: pool mappings (section_subcategories) can only change while DRAFT;
          // the backend rejects them for READY/OPEN/COMPLETED, so omit them entirely otherwise.
          if (paperStatus === "DRAFT") {
            requestBody.section_subcategories = buildSectionPoolsPayload().map((sp) => ({
              section_index: sp.section_index,
              session_key: sp.session_key,
              available_questions: sp.available_questions,
              no_of_questions: sp.no_of_questions,
              marks: sp.marks,
              blooms_level: sp.blooms_level,
            }));
          }
        } else {
          // Legacy weightage papers.
          requestBody.subcategories = pickedRows.map((item) => ({
            subcategory_id: item.subcategory_id,
            subcategory_name: item.subcategory_name,
            no_of_questions: Number(item.weight) || 0,
            question_type: item.question_type,
            session_key: item.session_key || "",
            available_questions: item.available_questions || 0,
            available_percentage: item.available_percentage || 0,
          }));
        }

        const res = await api.post("UpdateQuestionPaper", requestBody);
        // Pending-students warning → ask the user to approve completing anyway.
        if (isCompleteWarning(res)) {
          setCompleteWarning(res.data);
          setLoading(false);
          setSubmitting(false);
          return;
        }
        if (res?.data?.status === "success") {
          showSnackbar("success", `${getLabel('QUESTION_PAPER')} updated successfully!`);
          navigate(-1);
        } else {
          showSnackbar("error", res?.data?.message || `Failed to update ${getLabel('QUESTION_PAPER')}`);
        }
      } catch (error) {
        console.log("Error:", error);
        showSnackbar("error", error?.response?.data?.message || `Failed to update ${getLabel('QUESTION_PAPER')}`);
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    } else {
      setLoading(true);
      setSubmitting(true);
      try {
        const requestBody = {
          template_id: selectedTemplate?.template_id || null,
          paper_type: paperType,
          program_id: activeProgramId(),
          ...categoryScope(data.primaryCategory?.id || 0),
          test_type: data.examType === "Self Assessment" ? 1 : 2,
          time_limit: data.timeDuration ? `${data.timeDuration} Minutes` : "",
          academic_year: data.academicYear || "",
          camera_assigned_status: data.cameraEnabled === "Enabled" ? "YES" : "NO",
          // Single assessment date + optional "schedule for later" time (Ready → Open trigger).
          ...buildSchedulePayload(data),
          levels: data.levelFilter || [],
          question_types: data.questionType || [],
          marks: (data.marksFilter || []).map(Number),
          blooms_levels: data.bloomsFilter || [],
        };

        if (paperType === "RANDOM") {
          // RANDOM: per-section pool mappings (NOT `subcategories`).
          requestBody.section_subcategories = buildSectionPoolsPayload().map((sp) => ({
            section_index: sp.section_index,
            session_key: sp.session_key,
            available_questions: sp.available_questions,
            no_of_questions: sp.no_of_questions,
            marks: sp.marks,
            blooms_level: sp.blooms_level,
          }));
        } else {
          // FIXED: questions are added per-section via AddQuestionsToSection below.
          requestBody.subcategories = pickedRows.map((item) => ({ session_key: item.session_key || "" }));
        }

        const res = await api.post("SaveQuestionPaper", requestBody);
        if (res?.data?.status === "success") {
          const assessmentId = res?.data?.assessment_id;
          const createdSections = res?.data?.sections || [];

          // Persist the questions picked inline on this screen, per section (template order).
          // (RANDOM has no manual picks, so the loop is a no-op for it.)
          const addFailures = [];
          for (let i = 0; i < sections.length; i++) {
            const ps = createdSections[i];
            if (!ps) continue;
            const splitMark = sections[i].splitMark || [];
            const secMarks = sections[i].marks;
            const quizIds = [];
            const marksOverride = []; // marks forced to the section's intent (not quiz-bank marks)
            const pushOne = (q, mark) => { if (q) { quizIds.push(q.id); marksOverride.push(mark ?? 0); } };
            const pushOpt = (opt) => {
              if (!opt) return;
              if (opt.subs) opt.subs.forEach((sub, si) => pushOne(sub, splitMark[si] ?? sub?.marks ?? secMarks));
              else pushOne(opt, secMarks ?? opt.marks);
            };
            if (sections[i].pattern === "either-or") {
              sections[i].items.forEach((pr) => { pushOpt(pr.a); pushOpt(pr.b); });
            } else {
              sections[i].items.forEach((it) => {
                if (it.subs) it.subs.forEach((sub, si) => pushOne(sub, splitMark[si] ?? sub?.marks ?? secMarks)); // split group
                else pushOne(it, secMarks ?? it.marks);
              });
            }
            if (quizIds.length) {
              try {
                await api.post("AddQuestionsToSection", {
                  paper_section_id: ps.paper_section_id,
                  assessment_id: assessmentId,
                  quiz_ids: quizIds,
                  marks_override: marksOverride,
                });
              } catch (err) {
                console.log("AddQuestionsToSection error:", err);
                addFailures.push(`${sections[i].title}: ${err?.response?.data?.message || "could not add questions"}`);
              }
            }
          }

          // Stay in the create/list flow — the paper is saved as a DRAFT and is approved
          // later from the question-paper view screen.
          if (addFailures.length) {
            showSnackbar("warning", `Saved as draft — ${addFailures.join("; ")}`);
          } else {
            showSnackbar("success", res?.data?.message || `${getLabel('QUESTION_PAPER')} saved`);
          }
          navigate(-1);
        } else {
          showSnackbar("error", res?.data?.message || `Failed to save ${getLabel('QUESTION_PAPER')}`);
        }
      } catch (error) {
        console.log("Error:", error);
        showSnackbar("error", `Failed to save ${getLabel('QUESTION_PAPER')}`);
      } finally {
        setLoading(false);
        setSubmitting(false);
      }
    }
  };

  const commonTextFieldStyle = {
    "& .MuiOutlinedInput-root": {
      "& fieldset": { borderColor: "white" },
      "&:hover fieldset": { borderColor: "#d1d1d1" },
      "&.Mui-focused fieldset": { borderColor: "#1c97e3" },
    },
    // MUI X v8 DatePicker uses PickersOutlinedInput (not fieldset)
    "& .MuiPickersOutlinedInput-notchedOutline": { borderColor: "white" },
    "&:hover .MuiPickersOutlinedInput-notchedOutline": { borderColor: "#d1d1d1" },
    "& .Mui-focused .MuiPickersOutlinedInput-notchedOutline": { borderColor: "#1c97e3" },
  };

  const fieldBoxSx = { width: { xs: "100%", md: "calc(50% - 20px)" } };
  const fullBoxSx = { width: { xs: "100%", md: "100%" } };
  // 3-up width (gap: 5 ⇒ 40px) — Academic Year / Schedule Date / Schedule Time share one row.
  const thirdBoxSx = { width: { xs: "100%", md: "calc(33.33% - 27px)" } };

  // Reusable multi-select (checkbox Autocomplete) used by the four drill-down filters.
  // A "Select All" row toggles every option at once; the menu stays open on select.
  const SELECT_ALL = "__select_all__";
  const renderMultiSelect = ({ label, options, valueKey, placeholder, getOptionLabel }) => {
    const filterKey =
      valueKey === "levelFilter" ? "level" : valueKey === "marksFilter" ? "marks" : valueKey === "bloomsFilter" ? "blooms" : "questionType";
    const labelOf = getOptionLabel || ((o) => `${o}`);
    return (
      <Grid sx={fieldBoxSx}>
        <Typography>{label}</Typography>
        <Controller
          name={valueKey}
          control={control}
          render={({ field }) => {
            const value = field.value || [];
            const allSelected = options.length > 0 && value.length === options.length;
            const apply = (next) => {
              field.onChange(next);
              refetchWithFilters({ [filterKey]: next });
            };
            return (
              <Autocomplete
                multiple
                disableCloseOnSelect
                disabled={filtersDisabled}
                options={options.length ? [SELECT_ALL, ...options] : []}
                value={value}
                getOptionLabel={(o) => (o === SELECT_ALL ? "Select All" : labelOf(o))}
                isOptionEqualToValue={(o, v) => o === v}
                onChange={(_, val, reason, detail) => {
                  if (detail?.option === SELECT_ALL) {
                    apply(allSelected ? [] : [...options]); // toggle all
                    return;
                  }
                  apply(val.filter((o) => o !== SELECT_ALL));
                }}
                renderOption={(props, option, { selected }) => {
                  const isAll = option === SELECT_ALL;
                  return (
                    <li {...props}>
                      <Checkbox
                        checked={isAll ? allSelected : selected}
                        indeterminate={isAll && value.length > 0 && !allSelected}
                      />
                      {isAll ? "Select All" : labelOf(option)}
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField {...params} placeholder={placeholder} sx={commonTextFieldStyle} />
                )}
              />
            );
          }}
        />
      </Grid>
    );
  };

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      <Box>
        {/* Back Button */}
        <Grid
          item
          sx={{
            display: "flex",
            alignItems: "center",
            color: "#1c97e3",
            gap: "4px",
            cursor: "pointer",
            width: "40px",
            mb: 2,
          }}
          onClick={() => navigate(-1)}
        >
          <ArrowBackIcon sx={{ fontSize: "17px" }} />
          <Typography sx={{ fontWeight: 600, fontSize: "14px" }}>Back</Typography>
        </Grid>

        {/* Form Card */}
        <Box>
          {/* Header */}
          <Grid
            sx={{
              pb: 2,
              display: "flex",
              gap: 1,
              alignItems: "center",
              borderBottom: "1px solid #d1d1d1",
            }}
          >
            <Grid sx={{ backgroundColor: "#1c97e3", p: 0.5, borderRadius: "10px" }}>
              <NoteAddIcon sx={{ color: "white", fontSize: "35px" }} />
            </Grid>
            <Grid>
              <Typography sx={{ fontSize: "30px", fontWeight: "bold" }}>
                {isEditMode ? "Update" : "Add"} {getLabel("QUESTION_PAPER")}
              </Typography>
            </Grid>
          </Grid>

          {/* Form */}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ display: "flex", flexDirection: "column", rowGap: 3, p: 3 }}>
                {/* Paper type (create only) */}
                {!isEditMode && (
                  <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {[
                      { type: "FIXED", title: "Fixed Paper", desc: "Teacher picks the exact questions for each section" },
                      { type: "RANDOM", title: "Random Paper", desc: "System picks questions per section filters at preview / serve time" },
                    ].map((o) => (
                      <Box
                        key={o.type}
                        onClick={() => handlePaperTypeChange(o.type)}
                        sx={{
                          flex: "1 1 260px",
                          maxWidth: 380,
                          cursor: "pointer",
                          borderRadius: "12px",
                          p: 2,
                          display: "flex",
                          gap: 1.5,
                          alignItems: "flex-start",
                          border: paperType === o.type ? "1px solid #1c97e3" : "1px solid #d6dde6",
                          boxShadow: paperType === o.type ? "0 0 0 2px rgba(28,151,227,0.15)" : "none",
                        }}
                      >
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            mt: "2px",
                            flex: "0 0 auto",
                            border: paperType === o.type ? "5px solid #1c97e3" : "2px solid #c8ced8",
                          }}
                        />
                        <Box>
                          <Typography sx={{ fontWeight: 700 }}>{o.title}</Typography>
                          <Typography sx={{ fontSize: "12px", color: "#8E9EBC" }}>{o.desc}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}

                {/* Choose Template — shown at the top; create, or editing a FIXED paper (locked) */}
                {(!isEditMode || paperType === "FIXED") && (
                  <>
                    <Grid sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                      <DescriptionIcon sx={{ color: "#1c97e3", fontSize: "26px" }} />
                      <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                        {isEditMode ? "Template" : "Choose Template"}
                      </Typography>
                    </Grid>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 5, alignItems: "flex-start", mb: 2 }}>
                      <Grid sx={fieldBoxSx}>
                        <Autocomplete
                          options={templates}
                          disabled={isEditMode}
                          getOptionLabel={(o) => o?.template_name || o?.name || ""}
                          isOptionEqualToValue={(o, v) => o?.template_id === v?.template_id}
                          value={selectedTemplate}
                          onChange={(_, val) => handleTemplateChange(val)}
                          renderInput={(params) => (
                            <TextField {...params} placeholder={`Select a saved ${getLabel('TEMPLATE')}`} sx={commonTextFieldStyle} />
                          )}
                        />
                      </Grid>
                      {selectedTemplate && (
                        <Grid sx={fieldBoxSx}>
                          <Box sx={{ border: "1px solid #d6dde6", borderRadius: "10px", p: 1.5 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: "12px" }}>{templateSummary(selectedTemplate).parts}</Typography>
                            <Typography sx={{ color: "#8E9EBC", fontSize: "12px", mt: 0.5 }}>
                              Total: {templateSummary(selectedTemplate).total} Marks · Format: {selectedTemplate.format}
                            </Typography>
                          </Box>
                        </Grid>
                      )}
                    </Box>
                  </>
                )}

                {/* General Information Section */}
                <Grid sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.5 }}>
                  <Grid>
                    <InfoIcon sx={{ color: "#1c97e3", fontSize: "30px", fontWeight: "bold" }} />
                  </Grid>
                  <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>General Information</Typography>
                </Grid>
                <Typography sx={{ fontSize: "13px", color: "#8E9EBC", mb: 2 }}>
                  This banks comes from Existing &amp; Mapped {getLabel('QUESTION_BANK')}
                </Typography>

                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                  {/* Program (create only) */}
                  {!isEditMode && chooseFromExistingQB && (
                    <Grid sx={fieldBoxSx}>
                      <Typography>{getLabel('PROGRAMS')} <span style={{ color: "red" }}>*</span></Typography>
                      <Controller
                        name="program"
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            options={programs}
                            getOptionLabel={(option) => {
                              if (!option) return "";
                              const label = option.display_name || option.program_name || "";
                              return option.program_code ? `[${option.program_code}] ${label}` : label;
                            }}
                            isOptionEqualToValue={(o, v) => o?.program_id === v?.program_id}
                            value={field.value}
                            onChange={(_, val) => {
                              field.onChange(val);
                              // Reset category + filters + breakdown when program changes
                              setValue("primaryCategory", null);
                              setValue("levelFilter", []);
                              setValue("questionType", []);
                              setValue("marksFilter", []);
                              setValue("bloomsFilter", []);
                              setLevelOptions([]);
                              setQuestionTypeOptions([]);
                              setMarksOptions([]);
                              setWeightages([]);
                              fetchCategoriesByProgram(val?.program_id);
                            }}
                            renderOption={(props, option) => (
                              <li {...props} key={option.program_id}>
                                {option.program_code ? `[${option.program_code}] ` : ""}
                                {option.display_name || option.program_name}
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder={`Select ${getLabel('PROGRAMS')}`}
                                sx={commonTextFieldStyle}
                                error={!!errors.program}
                                helperText={errors.program?.message}
                              />
                            )}
                          />
                        )}
                      />
                    </Grid>
                  )}

                  {/* Assigned Question Banks (Mapped/Assigned question bank mode) */}
                  {!chooseFromExistingQB && (
                    <Grid sx={fullBoxSx}>
                      <Typography>
                        List of assigned {getLabel('QUESTION_BANK')}{" "}
                        {!isEditMode && <span style={{ color: "red" }}>*</span>}
                      </Typography>
                      <Controller
                        name="assignedQuestionBank"
                        control={control}
                        render={({ field }) => {
                          const picked = field.value || [];
                          // Banks may span subjects — any combination is selectable.
                          const subjectCount = new Set(picked.map((q) => q.category)).size;
                          return (
                            <Autocomplete
                              multiple
                              disableCloseOnSelect
                              limitTags={1}
                              options={assignedQuestionBanks}
                              getOptionLabel={assignedQBLabel}
                              isOptionEqualToValue={(o, v) => o?.session_key === v?.session_key}
                              value={picked}
                              onChange={(_, val) => {
                                field.onChange(val);
                                // The banks identify the subject — bind it so the breakdown
                                // (CreateQuestionPaper) and the section browser
                                // (BrowseQuestionsForSection) can both run, and reset the
                                // filters since their option lists belong to the old selection.
                                // In edit the paper already has a subject and program; picking
                                // banks only narrows what you can browse (via ids[]), it must not
                                // rewrite the saved paper's identity. In create the banks are the
                                // only source of both, so bind them.
                                const cat = isEditMode
                                  ? getValues("primaryCategory")
                                  : categoryFromAssignedQBs(val);
                                if (!isEditMode) {
                                  setValue("primaryCategory", cat);
                                  setValue("program", programFromAssignedQBs(val));
                                }
                                setValue("levelFilter", []);
                                setValue("questionType", []);
                                setValue("marksFilter", []);
                                setValue("bloomsFilter", []);
                                if (cat) {
                                  fetchBreakdown(cat, {}, false);
                                } else {
                                  setWeightages([]);
                                  setLevelOptions([]);
                                  setQuestionTypeOptions([]);
                                  setMarksOptions([]);
                                }
                              }}
                              renderOption={(props, option, { selected }) => {
                                const { key, ...optionProps } = props;
                                return (
                                  <Box
                                    component="li"
                                    {...optionProps}
                                    key={option.session_key}
                                    sx={{ display: "flex !important", alignItems: "center", gap: 1, fontSize: 13 }}
                                  >
                                    <Checkbox checked={selected} sx={{ p: 0.5, mr: 0.5 }} />
                                    <Chip size="small" label={option.level || "—"} sx={{ height: 20, fontSize: 10.5, fontWeight: 700, backgroundColor: "#fef3c7", color: "#d97706", flexShrink: 0 }} />
                                    <Chip size="small" label={option.question_type || "—"} sx={{ height: 20, fontSize: 10.5, fontWeight: 700, backgroundColor: "#e3f2fd", color: "#1c97e3", flexShrink: 0 }} />
                                    {/* flex:1 + minWidth:0 lets the long label truncate instead of
                                      pushing the right-hand group out of view. */}
                                    <span style={{ flex: 1, minWidth: 0, color: "#1f2937", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {assignedQBLabel(option)}
                                    </span>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0, ml: 1 }}>
                                      {option.total_questions != null && (
                                        <span style={{ color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap" }}>
                                          {option.total_questions} Q
                                        </span>
                                      )}
                                      {/* No logged-in institute (e.g. Super Admin) means there is
                                        nothing to compare against — omit rather than call
                                        every bank "Others". */}
                                      {loginInstituteId != null && (
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                          <span style={{ color: "#94a3b8", fontSize: 11, whiteSpace: "nowrap" }}>Created by</span>
                                          <Tooltip title={option.institute_name || ""} arrow>
                                            <Chip
                                              size="small"
                                              label={isOwnInstituteBank(option) ? "Own" : "Others"}
                                              sx={{
                                                height: 20,
                                                fontSize: 10.5,
                                                fontWeight: 700,
                                                flexShrink: 0,
                                                backgroundColor: isOwnInstituteBank(option) ? "#dcfce7" : "#fff7ed",
                                                color: isOwnInstituteBank(option) ? "#16a34a" : "#ea580c",
                                              }}
                                            />
                                          </Tooltip>
                                        </Box>
                                      )}
                                    </Box>
                                  </Box>
                                );
                              }}
                              renderTags={(value, getTagProps) =>
                                value.map((option, index) => {
                                  const { key, ...tagProps } = getTagProps({ index });
                                  return (
                                    <Chip
                                      {...tagProps}
                                      key={option.session_key}
                                      size="small"
                                      label={`${option.subcategory || "—"} · ${option.level || "—"} · ${option.question_type || "—"}`}
                                      sx={{ height: 24, fontSize: 11, fontWeight: 600, backgroundColor: "#e3f2fd", color: "#1c97e3", maxWidth: 280 }}
                                    />
                                  );
                                })
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder={picked.length ? "" : `Select assigned ${getLabel('QUESTION_BANK')}`}
                                  sx={commonTextFieldStyle}
                                  error={!!errors.assignedQuestionBank}
                                  helperText={
                                    errors.assignedQuestionBank?.message ||
                                    (picked.length
                                      ? `${picked.length} bank${picked.length === 1 ? "" : "s"} selected across ${subjectCount} subject${subjectCount === 1 ? "" : "s"}.`
                                      : " ")
                                  }
                                />
                              )}
                            />
                          );
                        }}
                      />
                    </Grid>
                  )}

                  {/* Category */}
                  {/* Subject is not shown: create derives it from the selected question
                      banks, and update keeps whatever the saved paper already has. The
                      primaryCategory form value is still what scopes the breakdown. */}

                  {/* Exam Type */}
                  <Grid sx={fieldBoxSx}>
                    <Typography>{getLabel('EXAM')} Type <span style={{ color: "red" }}>*</span></Typography>
                    <Controller
                      name="examType"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          readOnly={isEditMode}
                          options={["Self Assessment", "Proctored Assessments"]}
                          value={field.value}
                          onChange={(_, val) => field.onChange(val)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder={`Select ${getLabel('EXAM')} Type`}
                              sx={commonTextFieldStyle}
                              error={!!errors.examType}
                              helperText={errors.examType?.message}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>

                  {/* Exam with Camera */}
                  <Grid sx={fieldBoxSx}>
                    <Typography>{getLabel('EXAM')} with Camera <span style={{ color: "red" }}>*</span></Typography>
                    <Controller
                      name="cameraEnabled"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          readOnly={isEditMode}
                          options={["Enabled", "Disabled"]}
                          value={field.value}
                          onChange={(_, val) => field.onChange(val)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              placeholder="Select Camera Setting"
                              sx={commonTextFieldStyle}
                              error={!!errors.cameraEnabled}
                              helperText={errors.cameraEnabled?.message}
                            />
                          )}
                        />
                      )}
                    />
                  </Grid>

                  {/* Time Duration (minutes) */}
                  <Grid sx={fieldBoxSx}>
                    <Typography>Time Duration <span style={{ color: "red" }}>*</span></Typography>
                    <Controller
                      name="timeDuration"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          type="number"
                          placeholder="Enter duration in minutes"
                          InputProps={{
                            readOnly: isEditMode,
                            endAdornment: <Typography sx={{ fontSize: "12px", color: "#8E9EBC", mr: 1 }}>Minutes</Typography>,
                          }}
                          sx={commonTextFieldStyle}
                          fullWidth
                          error={!!errors.timeDuration}
                          helperText={errors.timeDuration?.message}
                        />
                      )}
                    />
                  </Grid>

                  {/* Academic Year (numbers + special characters only) */}
                  <Grid sx={thirdBoxSx}>
                    <Typography>Academic Year</Typography>
                    <Controller
                      name="academicYear"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          InputProps={{ readOnly: isEditMode }}
                          placeholder="e.g. 2025 - 2026"
                          onChange={(e) => field.onChange(e.target.value.replace(/[a-zA-Z]/g, ""))}
                          sx={commonTextFieldStyle}
                          fullWidth
                        />
                      )}
                    />
                  </Grid>

                  {/* Schedule Date */}
                  <Grid sx={thirdBoxSx}>
                    <Typography>Schedule Date <span style={{ color: "red" }}>*</span></Typography>
                    <Controller
                      name="latestDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          format="DD-MM-YYYY"
                          disabled={isEditMode && isStatusOpen}
                          minDate={isEditMode ? null : dayjs()}
                          value={field.value ? (dayjs.isDayjs(field.value) ? field.value : dayjs(field.value)) : null}
                          onChange={(newValue) => {
                            if (status && status.toUpperCase() === "OPEN" && newValue) {
                              const selectedDate = dayjs(newValue).startOf("day");
                              const today = dayjs().startOf("day");
                              if (!selectedDate.isSame(today)) {
                                setError("status", {
                                  type: "manual",
                                  message: "Schedule date should be today when status is OPEN",
                                });
                                return;
                              }
                            }
                            if (errors.status) setError("status", null);
                            field.onChange(newValue);
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.latestDate,
                              helperText: errors.latestDate?.message,
                              placeholder: "DD-MM-YYYY",
                              sx: commonTextFieldStyle,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  {/* Schedule Time — sits in the same row as Academic Year + Schedule Date. */}
                  <Grid sx={thirdBoxSx}>
                    <Typography>Schedule Time <span style={{ color: "red" }}>*</span></Typography>
                    <Controller
                      name="scheduleTime"
                      control={control}
                      render={({ field }) => (
                        <TimePicker
                          {...field}
                          disabled={isEditMode && isStatusOpen}
                          value={field.value ? (dayjs.isDayjs(field.value) ? field.value : dayjs(field.value)) : null}
                          onChange={(v) => field.onChange(v)}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              error: !!errors.scheduleTime,
                              helperText: errors.scheduleTime?.message,
                              placeholder: "hh:mm AM/PM",
                              sx: commonTextFieldStyle,
                            },
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Typography sx={{ width: "100%", fontSize: 12, color: "#8e9ebc", mt: 0.5 }}>
                    The {getLabel('EXAM')} opens at this time only when it is Ready and at least one batch is assigned.
                  </Typography>

                  {/* Status (edit only) */}
                  {isEditMode && (
                    <Grid sx={fieldBoxSx}>
                      <Typography>Status <span style={{ color: "red" }}>*</span></Typography>
                      <Controller
                        name="status"
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            options={statusOptions}
                            value={statusOptions.find((o) => o.value === field.value) || null}
                            isOptionEqualToValue={(option, val) => option.value === val.value}
                            onChange={(_, val) => {
                              if (val && val.value === "OPEN" && latestDate) {
                                const scheduleDate = dayjs(latestDate).startOf("day");
                                const today = dayjs().startOf("day");
                                if (!scheduleDate.isSame(today)) {
                                  setError("status", {
                                    type: "manual",
                                    message: "Schedule date should be today to change status to OPEN",
                                  });
                                  return;
                                }
                              }
                              if (errors.status) setError("status", null);
                              field.onChange(val ? val.value : null);
                              if (!val || val.value !== "CANCELLED") {
                                setValue("cancelReason", "");
                                setCancelReason("");
                              }
                            }}
                            getOptionLabel={(option) => option?.label || ""}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Select Status"
                                sx={commonTextFieldStyle}
                                error={!!errors.status}
                                helperText={errors.status?.message}
                              />
                            )}
                          />
                        )}
                      />
                    </Grid>
                  )}
                </Box>

                {/* Cancel Reason Field - Only show when status is Cancelled */}
                {isEditMode && status === "CANCELLED" && (
                  <Grid sx={{ mt: 2 }}>
                    <Typography>Cancellation Reason <span style={{ color: "red" }}>*</span></Typography>
                    <Controller
                      name="cancelReason"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          multiline
                          rows={3}
                          placeholder="Please provide the reason for cancellation..."
                          fullWidth
                          onChange={(e) => {
                            field.onChange(e);
                            setCancelReason(e.target.value);
                          }}
                          sx={commonTextFieldStyle}
                          error={!!errors.cancelReason}
                          helperText={errors.cancelReason?.message}
                        />
                      )}
                    />
                  </Grid>
                )}

                {/* Drill-down Filters — available in create and edit, for both paper types.
                    Legacy weightage papers have no template and are filtered by the
                    weightage editor's own rows, so they keep the block hidden. */}
                {(selectedTemplate || !isEditMode) && (
                  <>
                    <Grid sx={{ display: "flex", gap: 1, alignItems: "center", mt: 2 }}>
                      <FilterAltIcon sx={{ color: "#1c97e3", fontSize: "26px" }} />
                      <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>Filters</Typography>
                      <Tooltip title="Narrow the available questions below. Leave empty to see everything." arrow>
                        <InfoOutlinedIcon sx={{ fontSize: "16px", color: "#8E9EBC" }} />
                      </Tooltip>
                    </Grid>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                      {renderMultiSelect({
                        label: "Level",
                        options: levelOptions,
                        valueKey: "levelFilter",
                        placeholder: "All Levels",
                      })}
                      {renderMultiSelect({
                        label: "Question Type",
                        options: questionTypeOptions,
                        valueKey: "questionType",
                        placeholder: "All Question Types",
                      })}
                      {paperType !== "RANDOM" &&
                        renderMultiSelect({
                          label: "Marks",
                          options: marksOptions,
                          valueKey: "marksFilter",
                          placeholder: "All Marks",
                          getOptionLabel: (o) => `${o} ${Number(o) === 1 ? "Mark" : "Marks"}`,
                        })}
                      {renderMultiSelect({
                        label: "Bloom's Level",
                        options: bloomsOptions,
                        valueKey: "bloomsFilter",
                        placeholder: "All Bloom's Levels",
                      })}
                    </Box>
                  </>
                )}

                {/* Section-wise question browser — template is chosen at the top of the form.
                    BrowseQuestionsForSection runs per active section; picks reflect live in the preview. */}
                {paperType === "FIXED" && selectedTemplate && (
                  <Box sx={{ mt: 2 }}>
                    <Grid sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}>
                      <AssignmentIcon sx={{ color: "#1c97e3", fontSize: "26px" }} />
                      <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>Add Questions</Typography>
                    </Grid>
                    <AddQuestionsStep
                      sections={sections}
                      activeIdx={activeIdx}
                      setActiveIdx={setActiveIdx}
                      onSectionsChange={setSections}
                      category={selectedCategory}
                      assignedQuestionBanks={watch("assignedQuestionBank")}
                      assessmentId={location.state?.assessment_id || null}
                      chooseFromExistingQB={chooseFromExistingQB}
                      filters={{
                        level: watch("levelFilter") || [],
                        question_types: watch("questionType") || [],
                        marks: watch("marksFilter") || [],
                        blooms_levels: watch("bloomsFilter") || [],
                      }}
                    />
                  </Box>
                )}

                {/* RANDOM — per-section pool assignment (create + new-format edit) */}
                {paperType === "RANDOM" && selectedTemplate && (
                  <Box sx={{ mt: 2 }}>
                    <Grid sx={{ display: "flex", gap: 1, alignItems: "center", mb: 0.5 }}>
                      <AssignmentIcon sx={{ color: "#1c97e3", fontSize: "26px" }} />
                      <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>Assign Pools to Sections</Typography>
                    </Grid>
                    <Typography sx={{ fontSize: "13px", color: "#7b8794", mb: 2 }}>
                      For each section choose the subtopic pool(s) to draw from and how many questions.
                      The assigned counts must equal the section's required slots.
                    </Typography>

                    {poolOptions.length === 0 && (
                      <Box sx={{ p: 3, mb: 2, backgroundColor: "#fff8e1", border: "1px solid #ffe082", borderRadius: "10px" }}>
                        <Typography sx={{ fontSize: "13px", color: "#92400e", fontWeight: 600 }}>
                          {chooseFromExistingQB
                            ? `Select a ${getLabel('PROGRAMS')} and ${getLabel('SUBJECT')} (and optionally filters) above to load the available pools.`
                            : `Select one or more assigned ${getLabel('QUESTION_BANK')} above to load the available pools.`}
                        </Typography>
                      </Box>
                    )}

                    {(selectedTemplate.parts || []).map((part, si) => {
                      const need = sectionSlots[si];
                      const sum = poolSum(si);
                      const ok = sectionPoolOk(si);
                      return (
                        <Card key={part.partkey || part.key || si} sx={{ p: 2, mb: 2, borderRadius: "12px", border: "1px solid #e3e9f0", boxShadow: "0 2px 6px rgba(20,40,80,0.04)" }}>
                          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, flexWrap: "wrap", mb: 1 }}>
                            <Box>
                              <Typography sx={{ fontWeight: 800, fontSize: "15px" }}>{part.title}</Typography>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                                <Chip size="small" label={part.pattern === "list" ? "LIST" : "EITHER-OR"} sx={{ height: 20, fontSize: 10, fontWeight: 800, backgroundColor: part.pattern === "list" ? "#e0f2fe" : "#f3e8ff", color: part.pattern === "list" ? "#075985" : "#6b21a8" }} />
                                <Typography sx={{ fontSize: "12.5px", color: "#7b8794" }}>{part.marks} marks each · needs <b>{need}</b> question slots</Typography>
                              </Box>
                            </Box>
                            <Chip size="small" label={`${ok ? "✓" : "⚠"} Assigned ${sum} / ${need}`} sx={{ fontWeight: 700, backgroundColor: ok ? "#dcfce7" : "#ffebee", color: ok ? "#166534" : "#991b1b", border: ok ? "1px solid #a7f3d0" : "1px solid #fecaca" }} />
                          </Box>

                          {partGroups(part).map((g) => {
                            const gRows = groupRowsWithIdx(si, g.mark);
                            const gSum = groupSum(si, g.mark);
                            const gok = groupOk(si, g);
                            const groupPoolOptions = poolOptions.filter((o) => Number(o.marks) === Number(g.mark));
                            // A session_key (QB) may only be used in ONE section per marks value
                            // (backend rejects reuse). Collect session_keys already chosen by OTHER
                            // sections at this same mark so we hide the whole QB from this dropdown.
                            const usedSessionKeysOtherSections = new Set();
                            sectionPools.forEach((rows, oi) => {
                              if (oi === si) return;
                              (rows || []).forEach((p) => {
                                if (p.session_key && Number(p.group_mark) === Number(g.mark)) {
                                  usedSessionKeysOtherSections.add(p.session_key);
                                }
                              });
                            });
                            const hasSplit = (part.split_mark || []).length > 0;
                            return (
                              <Box
                                key={g.mark}
                                sx={{
                                  mb: 1.5,
                                  p: hasSplit ? 1.25 : 0,
                                  borderRadius: "10px",
                                  border: hasSplit ? "1px dashed #d8e2ee" : "none",
                                  backgroundColor: hasSplit ? "#fbfdff" : "transparent",
                                }}
                              >
                                {hasSplit && (
                                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                      <Chip size="small" label={`${g.mark} mark`} sx={{ height: 20, fontSize: 10.5, fontWeight: 800, backgroundColor: "#ede9fe", color: "#7c3aed" }} />
                                      <Typography sx={{ fontSize: "12.5px", color: "#7b8794" }}>needs <b>{g.slots}</b> question{g.slots === 1 ? "" : "s"}</Typography>
                                    </Box>
                                    <Chip size="small" label={`${gok ? "✓" : "⚠"} ${gSum} / ${g.slots}`} sx={{ height: 22, fontWeight: 700, fontSize: 11, backgroundColor: gok ? "#dcfce7" : "#ffebee", color: gok ? "#166534" : "#991b1b", border: gok ? "1px solid #a7f3d0" : "1px solid #fecaca" }} />
                                  </Box>
                                )}

                                {gRows.map(({ row, ri }) => {
                                  const pool = rowOption(row);
                                  const own = Number(row.no_of_questions) || 0;
                                  // What this row may still claim: the bank's total minus what
                                  // the section's other rows already take from the same bank.
                                  const rowMax = rowAvailable(si, ri, row);
                                  const over = pool && own > rowMax;
                                  const rowOptions = groupPoolOptions.filter(
                                    (o) =>
                                      o.id === rowId(row) || // keep the current selection visible
                                      // not taken by another row in this group, and the QB isn't used by another same-mark section
                                      (!gRows.some(({ row: p, ri: j }) => j !== ri && rowId(p) === o.id) &&
                                        !usedSessionKeysOtherSections.has(o.session_key))
                                  );
                                  // Keep the current selection visible even if a filter excluded it.
                                  if (pool && !rowOptions.some((o) => o.id === pool.id)) rowOptions.unshift(pool);
                                  return (
                                    <Box
                                      key={ri}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1.5,
                                        mb: 1.25,
                                        p: 1.25,
                                        borderRadius: "10px",
                                        backgroundColor: "#fafbfc",
                                        border: "1px solid #eef1f5",
                                        flexWrap: { xs: "wrap", md: "nowrap" },
                                      }}
                                    >
                                      {/* Pool picker — searchable autocomplete with rich options */}
                                      <Autocomplete
                                        size="small"
                                        options={rowOptions}
                                        value={pool || null}
                                        getOptionLabel={(o) => o?.label || ""}
                                        isOptionEqualToValue={(o, v) => o.id === v?.id}
                                        onChange={(_, val) => setPoolOption(si, ri, val)}
                                        sx={{ flex: 3, minWidth: 240 }}
                                        renderOption={(props, o, { index }) => (
                                          <Box
                                            component="li"
                                            {...props}
                                            key={`${o.session_key}-${o.blooms_level || ""}-${o.marks ?? ""}-${index}`}
                                            sx={{ display: "flex !important", justifyContent: "space-between", alignItems: "center", gap: 1.5, fontSize: 13 }}
                                          >
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
                                              <Chip size="small" label={`${o.marks ?? "—"}m`} sx={{ height: 20, fontSize: 10.5, fontWeight: 700, backgroundColor: "#ede9fe", color: "#7c3aed", flexShrink: 0 }} />
                                              <span style={{ color: "#1f2937", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</span>
                                            </Box>
                                            <span style={{ color: "#94a3b8", fontSize: 12, whiteSpace: "nowrap" }}>{o.available_questions} avail</span>
                                          </Box>
                                        )}
                                        renderInput={(params) => (
                                          <TextField
                                            {...params}
                                            placeholder="Select subtopic pool"
                                            InputProps={{
                                              ...params.InputProps,
                                              startAdornment: (
                                                <>
                                                  {pool && (
                                                    <Chip size="small" label={`${pool.marks ?? "—"}m`} sx={{ height: 20, fontSize: 10.5, fontWeight: 700, backgroundColor: "#ede9fe", color: "#7c3aed", mr: 0.5, flexShrink: 0 }} />
                                                  )}
                                                  {params.InputProps.startAdornment}
                                                </>
                                              ),
                                            }}
                                            sx={{
                                              // match the project's borderless field style (template/filter dropdowns)
                                              "& .MuiOutlinedInput-root": {
                                                borderRadius: "10px",
                                                backgroundColor: "#fff",
                                                "& fieldset": { borderColor: "#e3e9f0" },
                                                "&:hover fieldset": { borderColor: "#d1d1d1" },
                                                "&.Mui-focused fieldset": { borderColor: "#1c97e3" },
                                              },
                                            }}
                                          />
                                        )}
                                      />
                                      {/* Count — project-styled rounded accent field */}
                                      <TextField
                                        size="small"
                                        type="number"
                                        value={row.no_of_questions}
                                        // Cap at the pool's remaining capacity so it can't be over-allocated cross-section.
                                        onChange={(e) => setPoolCount(si, ri, e.target.value === "" ? "" : Math.max(0, Math.min(rowMax, Number(e.target.value))))}
                                        inputProps={{ min: 0, max: rowMax, style: { textAlign: "center", fontWeight: 800, fontSize: 14, color: over ? "#d32f2f" : "#1c97e3" } }}
                                        sx={{
                                          width: 92,
                                          "& .MuiOutlinedInput-root": {
                                            borderRadius: "10px",
                                            backgroundColor: over ? "#fff0f0" : "#eef6fe",
                                            "& fieldset": { borderColor: over ? "#f1aeae" : "transparent" },
                                            "&:hover fieldset": { borderColor: over ? "#d32f2f" : "#bcdffb" },
                                            "&.Mui-focused fieldset": { borderColor: over ? "#d32f2f" : "#1c97e3" },
                                          },
                                        }}
                                      />
                                      <Typography sx={{ fontSize: "12px", color: "#64748b", whiteSpace: "nowrap", flex: 1 }}>
                                        of <b style={{ color: "#1f2937" }}>{pool ? rowMax : "?"}</b> available
                                      </Typography>
                                      <IconButton
                                        aria-label="Remove pool"
                                        size="small"
                                        onClick={() => removePoolRow(si, ri)}
                                        sx={{ border: "1px solid #e3e9f0", borderRadius: "8px", color: "#94a3b8", "&:hover": { color: "#d32f2f", backgroundColor: "#fff0f0", borderColor: "#f1aeae" } }}
                                      >
                                        <CloseIcon sx={{ fontSize: 16 }} />
                                      </IconButton>
                                    </Box>
                                  );
                                })}

                                {gRows.some(({ row: r, ri: i }) => rowOption(r) && Number(r.no_of_questions) > rowAvailable(si, i, r)) && (
                                  <Typography sx={{ fontSize: "12.5px", color: "#b45309", backgroundColor: "#fff8e1", border: "1px solid #ffe082", borderRadius: "8px", p: 1, mt: 0.5, mb: 1 }}>
                                    ⚠ A pool requests more questions than are available — rows sharing the same
                                    question bank draw from one set of questions. The backend will reject this.
                                  </Typography>
                                )}

                                <Button
                                  size="small"
                                  startIcon={<AddIcon />}
                                  onClick={() => addPoolRow(si, g.mark)}
                                  sx={{
                                    textTransform: "none",
                                    fontWeight: 700,
                                    color: "#1c97e3",
                                    backgroundColor: "#eef6fe",
                                    borderRadius: "8px",
                                    px: 1.5,
                                    mt: 0.5,
                                    "&:hover": { backgroundColor: "#dcecfb" },
                                  }}
                                >
                                  {hasSplit ? `Add ${g.mark}-mark pool` : "Add pool"}
                                </Button>
                              </Box>
                            );
                          })}
                        </Card>
                      );
                    })}
                  </Box>
                )}

                {/* Curriculum Weightage Section — legacy RANDOM edit flow (papers without pools[]) */}
                {paperType === "RANDOM" && isEditMode && !selectedTemplate && (
                  <>
                    <Grid sx={{ display: "flex", gap: 1, justifyContent: "space-between", alignItems: "center", mb: 1, mt: 2 }}>
                      <Grid sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <AssignmentIcon sx={{ color: "#1c97e3", fontSize: "30px", fontWeight: "bold" }} />
                        <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>Curriculum Weightage</Typography>
                      </Grid>
                      <Box sx={{ display: "flex", alignItems: "flex-end", mb: 1, flexDirection: "column", gap: 1 }}>
                        <Box sx={{ display: "flex", gap: 2 }}>
                          <Box
                            sx={{
                              backgroundColor: "#1c97e3",
                              color: "white",
                              px: 2,
                              py: 1,
                              borderRadius: "8px",
                              fontWeight: 700,
                              fontSize: "14px",
                            }}
                          >
                            Total Questions: {totalQuestionsCount}
                          </Box>
                        </Box>
                      </Box>
                    </Grid>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 3 }}>
                      {weightages.length === 0 ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "center",
                            alignItems: "center",
                            p: 4,
                            backgroundColor: "#f8f9fa",
                            borderRadius: "12px",
                            border: "1px dashed #d1d1d1",
                          }}
                        >
                          <AssignmentIcon sx={{ fontSize: "48px", color: "#cbd5e0", mb: 2 }} />
                          <Typography sx={{ color: "#2d3748", fontSize: "16px", fontWeight: 700, mb: 1 }}>
                            No Subtopics Loaded
                          </Typography>
                          <Typography sx={{ color: "#8E9EBC", fontSize: "14px", textAlign: "center", maxWidth: "400px" }}>
                            {isEditMode
                              ? `No subtopics found for this ${getLabel('QUESTION_PAPER')}.`
                              : `Select a Program and ${getLabel('SUBJECT')} to load available subtopics, then use the filters to narrow them.`}
                          </Typography>
                        </Box>
                      ) : (
                        weightages.map((group) => (
                          <Card
                            key={group.subcategory_id}
                            sx={{
                              p: 2,
                              borderRadius: "12px",
                              border: "1px solid #f1f3f6",
                              boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
                            }}
                          >
                            {/* Subcategory header */}
                            <Tooltip
                              title={group.description || ""}
                              arrow
                            >
                              <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
                                <Grid sx={{ backgroundColor: "#1c97e3", p: 0.75, borderRadius: "8px" }}>
                                  <AssignmentIcon sx={{ fontSize: "16px", color: "white" }} />
                                </Grid>
                                <Typography sx={{ fontWeight: 700, color: "#2d3748", fontSize: "15px" }}>
                                  {group.subcategory_name}
                                </Typography>
                                {group.description && <InfoIcon sx={{ fontSize: "16px", color: "#1c97e3" }} />}
                              </Box>
                            </Tooltip>

                            {/* Breakdown rows */}
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                              {group.breakdown.map((item) => {
                                const pickedPct = item.available_questions
                                  ? Math.round((Number(item.weight) || 0) / item.available_questions * 100)
                                  : 0;
                                return (
                                  <Box
                                    key={item.rowKey}
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 2,
                                      flexWrap: { xs: "wrap", md: "nowrap" },
                                      p: 1.5,
                                      backgroundColor: "#fafbfc",
                                      borderRadius: "10px",
                                      border: "1px solid #f1f3f6",
                                    }}
                                  >
                                    {/* Tags */}
                                    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center", flex: 2.5 }}>
                                      {item.question_type && (
                                        <Chip
                                          label={item.question_type}
                                          size="small"
                                          sx={{ height: "20px", fontSize: "10px", fontWeight: 700, backgroundColor: "#e3f2fd", color: "#1c97e3" }}
                                        />
                                      )}
                                      {item.level && (
                                        <Chip
                                          label={item.level}
                                          size="small"
                                          sx={{ height: "20px", fontSize: "10px", fontWeight: 700, backgroundColor: "#fef3c7", color: "#d97706" }}
                                        />
                                      )}
                                      {item.marks !== undefined && item.marks !== null && (
                                        <Chip
                                          label={`${item.marks} ${Number(item.marks) === 1 ? "Mark" : "Marks"}`}
                                          size="small"
                                          sx={{ height: "20px", fontSize: "10px", fontWeight: 700, backgroundColor: "#ede9fe", color: "#7c3aed" }}
                                        />
                                      )}
                                      <Chip
                                        label={item.blooms_level || "Untagged"}
                                        size="small"
                                        sx={{
                                          height: "20px",
                                          fontSize: "10px",
                                          fontWeight: 700,
                                          backgroundColor: item.blooms_level ? "#dcfce7" : "#f1f5f9",
                                          color: item.blooms_level ? "#16a34a" : "#64748b",
                                        }}
                                      />
                                      {/* Special question indicators (icon + chip) */}
                                      {item.lab_tool_id && (
                                        <Tooltip title="Image-based comprehension questions generated from a lab tool" arrow>
                                          <Chip
                                            icon={<ScienceIcon sx={{ fontSize: "12px !important" }} />}
                                            label={LAB_TOOL_LABELS[item.lab_tool_id] || "Lab Tool"}
                                            size="small"
                                            sx={{ height: "20px", fontSize: "10px", fontWeight: 700, backgroundColor: "#fee2e2", color: "#b91c1c" }}
                                          />
                                        </Tooltip>
                                      )}
                                      {item.reference_image_based_comprehension_questions && (
                                        <Tooltip title="Questions generated from a teacher-uploaded reference image" arrow>
                                          <Chip
                                            icon={<ImageIcon sx={{ fontSize: "12px !important" }} />}
                                            label="Reference Image"
                                            size="small"
                                            sx={{ height: "20px", fontSize: "10px", fontWeight: 700, backgroundColor: "#fff7ed", color: "#ea580c" }}
                                          />
                                        </Tooltip>
                                      )}
                                    </Box>

                                    {/* Available */}
                                    <Box sx={{ textAlign: "left", flex: 1 }}>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                        <InfoOutlinedIcon sx={{ fontSize: "10px", color: "#2d3748" }} />
                                        <Typography sx={{ fontSize: "8px", fontWeight: 700, color: "#2d3748", textTransform: "uppercase" }}>
                                          Available
                                        </Typography>
                                      </Box>
                                      <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#2d3748" }}>
                                        {item.available_questions}
                                      </Typography>
                                    </Box>

                                    {/* Picked % */}
                                    <Box sx={{ textAlign: "left", flex: 1 }}>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                                        <InfoOutlinedIcon sx={{ fontSize: "10px", color: "#2d3748" }} />
                                        <Typography sx={{ fontSize: "8px", fontWeight: 700, color: "#2d3748", textTransform: "uppercase" }}>
                                          Picked
                                        </Typography>
                                      </Box>
                                      <Chip
                                        label={`${pickedPct}%`}
                                        size="small"
                                        sx={{
                                          height: "20px",
                                          fontSize: "10px",
                                          fontWeight: 700,
                                          backgroundColor: pickedPct > 100 ? "#fee2e2" : "#e3f2fd",
                                          color: pickedPct > 100 ? "#991b1b" : "#1c97e3",
                                          borderRadius: "6px",
                                        }}
                                      />
                                    </Box>

                                    {/* Count input */}
                                    <Box sx={{ textAlign: "right", flex: 1.5 }}>
                                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5, mb: 0.5 }}>
                                        <InfoOutlinedIcon sx={{ fontSize: "10px", color: "#2d3748" }} />
                                        <Typography sx={{ fontSize: "8px", fontWeight: 700, color: "#2d3748", textTransform: "uppercase" }}>
                                          No. of Questions
                                        </Typography>
                                      </Box>
                                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                                        <TextField
                                          size="small"
                                          value={item.weight}
                                          disabled={isEditMode && isStatusOpen}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            if (val === "" || /^\d+$/.test(val)) {
                                              const numVal = val === "" ? "" : Number(val);
                                              if (numVal !== "") {
                                                if (numVal > item.available_questions) {
                                                  handleWeightChange(item.rowKey, item.available_questions);
                                                  showSnackbar("warning", `Cannot select more than available questions (${item.available_questions})`);
                                                } else if (numVal > MAX_PER_ROW) {
                                                  handleWeightChange(item.rowKey, MAX_PER_ROW);
                                                  showSnackbar("warning", `Maximum ${MAX_PER_ROW} questions allowed per row`);
                                                } else {
                                                  handleWeightChange(item.rowKey, val);
                                                }
                                              } else {
                                                handleWeightChange(item.rowKey, val);
                                              }
                                            }
                                          }}
                                          sx={{
                                            width: "70px",
                                            "& .MuiOutlinedInput-root": {
                                              backgroundColor: "#e3f2fd",
                                              borderRadius: "8px",
                                              "& fieldset": { border: "none" },
                                            },
                                          }}
                                          inputProps={{ style: { textAlign: "center", fontWeight: 700, fontSize: "12px" } }}
                                        />
                                      </Box>
                                    </Box>
                                  </Box>
                                );
                              })}
                            </Box>
                          </Card>
                        ))
                      )}
                    </Box>
                  </>
                )}

                {/* Buttons */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => navigate(-1)}
                    sx={{ textTransform: "none", borderRadius: "12px", px: 3 }}
                  >
                    Cancel
                  </Button>
                  {paperType === "RANDOM" && selectedTemplate && (
                    <Button
                      variant="outlined"
                      onClick={handleShowPreview}
                      disabled={loading || !randomValid}
                      sx={{ textTransform: "none", borderRadius: "12px", px: 3, borderColor: "#1c97e3", color: "#1c97e3" }}
                    >
                      View Template
                    </Button>
                  )}
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={loading || submitting || (paperType === "RANDOM" && selectedTemplate ? !randomValid : (totalQuestionsCount === 0 && pickedQuestionsCount === 0)) || !!errors.status}
                    sx={{
                      textTransform: "none",
                      borderRadius: "12px",
                      px: 4,
                      fontWeight: 600,
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    {isEditMode
                      ? `Update ${getLabel('QUESTION_PAPER')}`
                      : paperType === "FIXED"
                        ? `Save ${getLabel('QUESTION_PAPER')}`
                        : `Create ${getLabel('QUESTION_PAPER')}`}
                  </Button>
                </Box>
              </Box>
            </form>
          </LocalizationProvider>
        </Box>

        {/* Question Paper Template (Anna University format) — shown once a template is
            chosen (create) or always in edit. Starts with empty question slots. */}
        {(isEditMode || selectedTemplate) && (
          <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid #d1d1d1" }}>
            <Grid sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
              <Grid sx={{ backgroundColor: "#1c97e3", p: 0.5, borderRadius: "10px" }}>
                <DescriptionIcon sx={{ color: "white", fontSize: "30px" }} />
              </Grid>
              <Typography sx={{ fontSize: "20px", fontWeight: "bold" }}>
                {getLabel('QUESTION_PAPER')} {getLabel('TEMPLATE')}
              </Typography>
            </Grid>
            <QuestionPaperTemplate data={previewShown ? previewPaper : templateData} />
          </Box>
        )}
      </Box>

      {submitting && (
        <OverAllLoader
          message={isEditMode ? `Updating ${getLabel('QUESTION_PAPER')}...` : `Saving ${getLabel('QUESTION_PAPER')}...`}
        />
      )}

      {/* Completion confirmation — shown when the backend warns that students are still pending. */}
      <Dialog open={!!completeWarning} onClose={handleDeclineComplete} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Complete {getLabel('EXAM')}?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: 14, color: "#475569" }}>
            {completeWarning?.message ||
              `Some assigned ${getLabel('STUDENT')}s haven't completed this ${getLabel('EXAM')} yet. Do you want to mark it as completed anyway?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            color="secondary"
            onClick={handleDeclineComplete}
            sx={{ textTransform: "none", borderRadius: "10px" }}
          >
            Decline
          </Button>
          <Button
            variant="contained"
            onClick={handleApproveComplete}
            sx={{ textTransform: "none", borderRadius: "10px" }}
          >
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CreateQuestionPaper;
